import React, { useEffect, useState, useCallback, useContext, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Board from '../Board';
import styles from './TutorialGame.module.css';
import globalStyles from '../../App.module.css';
import ZodiacEngineService from '../ZodiacEngineService';
import AdSenseLayout from '../AdSense/AdSenseLayout';
import { loadTestScenarioFEN } from '../../services/zodiacEngine/Fen/FenLoader';
import TutorialProgressService from './TutorialProgressService';
import { getTutorialTitle, getTutorialDescription, TUTORIAL_TITLES } from './TutorialTitles';
import { getTutorialFEN, getTutorialPostProcessing } from './TutorialFENs';
import { applyTutorialPostProcessing } from './TutorialPostProcessing';
import { getTutorialTurnLimit, isTutorialSolo, tutorialHasNoDarkKingOverride, tutorialHasNoLightKingOverride } from './TutorialSpecs';
import { getTutorialTerrain, getTerrainImagePath, shouldTerrainRenderAsOverlay } from './Terrain';
import { IMove, ISideEffect, Position, Mode, State } from '../../services/zodiacEngine/types';
import { ZodiacFEN } from '../../services/zodiacEngine/Fen/ZodiacFEN';
import { GameContext } from '../Providers/GameContext';
import { checkTutorialWinCondition, getTutorialWinDescriptions, checkTutorialFailure, isSecretVictoryEligible, TUTORIAL_WIN_CONFIGS } from './TutorialWinConditions';
import TutorialLossPopup from './TutorialLossPopup';
import TutorialIntro from './TutorialIntro';
import { shouldStalemateTriggerLoss, getStalemateLossMessage, StalemateEventData } from './TutorialStalemateHandler';
import { getRandomSecretVictoryMessageRandom, getRandomRegularVictoryMessage } from './SecretVictoryMessages';
import { tutorialSoundEffects, LossType, TutorialSoundEffects } from '../../utils/TutorialSoundEffects';
import GlobalHeader from '../GlobalHeader';
import { useTheme } from '../Providers/ThemeContext';
import AIThinking from '../MCTSStats/AIThinking';
import AIPlanPanel from './AIPlanPanel';
import { useAuthContext } from '../../hooks/useAuthContext';
import { playUISound, playVictorySound, playDefeatSound } from '../../utils/UISoundEffects';

interface TutorialGameProps {}

// Bear-5 diagnostic helper. Scans a game board and emits a one-line census
// of light bears vs. dark rhinos (kings vs. non-kings). Used to detect
// whether the board passed into checkTutorialWinCondition has been mutated
// or cleared (an AI-state-isolation–shaped bug, ref memory/alchemist-mcts-state-isolation.md).
function bear5DiagCensus(board: any[][]): string {
  let lightBearKings = 0, lightBearsNonKing = 0;
  let darkRhinoKings = 0, darkRhinosNonKing = 0;
  let totalDark = 0;
  for (let r = 0; r < board.length; r++) {
    const row = board[r];
    if (!row) continue;
    for (let c = 0; c < row.length; c++) {
      const square = row[c];
      if (!square?.Pieces) continue;
      for (const p of square.Pieces) {
        if (!p) continue;
        if (!p.isLight) totalDark++;
        if (p.type === 'Bear' && p.isLight) {
          if (p.isKing) lightBearKings++; else lightBearsNonKing++;
        } else if (p.type === 'Rhino' && !p.isLight) {
          if (p.isKing) darkRhinoKings++; else darkRhinosNonKing++;
        }
      }
    }
  }
  return `lightBearKings=${lightBearKings} lightBearsNonKing=${lightBearsNonKing} darkRhinoKings=${darkRhinoKings} darkRhinosNonKing=${darkRhinosNonKing} totalDark=${totalDark}`;
}

// Bear-4 diagnostic helper. Bear 4 starting FEN has bear king + monkey + wolf
// (Light) and one dark crane king. The secret-win triggers on "dark crane
// absent" — so the census focuses on whether the dark crane king is visible
// in the snapshot, plus total piece count to detect cleared/stale boards.
function bear4DiagCensus(board: any[][]): string {
  let lightBearKing = 0;
  let darkCraneKing = 0, darkCranesNonKing = 0;
  let totalPieces = 0;
  let craneAt = '';
  let bearAt = '';
  for (let r = 0; r < board.length; r++) {
    const row = board[r];
    if (!row) continue;
    for (let c = 0; c < row.length; c++) {
      const square = row[c];
      if (!square?.Pieces) continue;
      for (const p of square.Pieces) {
        if (!p) continue;
        totalPieces++;
        if (p.type === 'Bear' && p.isLight && p.isKing) { lightBearKing++; bearAt = `(${r},${c})`; }
        if (p.type === 'Crane' && !p.isLight) {
          if (p.isKing) { darkCraneKing++; craneAt = `(${r},${c})`; }
          else darkCranesNonKing++;
        }
      }
    }
  }
  return `lightBearKing=${lightBearKing}${bearAt ? '@' + bearAt : ''} darkCraneKing=${darkCraneKing}${craneAt ? '@' + craneAt : ''} darkCranesNonKing=${darkCranesNonKing} totalPieces=${totalPieces}`;
}

function TutorialGame({}: TutorialGameProps) {
  const { constellation, tutorialIndex } = useParams<{
    constellation: string;
    tutorialIndex: string;
  }>();
  const navigate = useNavigate();
  // AI Plan Panel is dev-only — gated to the maintainer's account so regular
  // players don't see the AI's principal-variation readout.
  const { userProfile, user } = useAuthContext();
  // Dev-only panel. Visible in dev builds, or for the maintainer account
  // (username startsWith 'dscvr' covers 'dscvr', 'dscvr10', etc., or email).
  const showAIPlanPanel =
    process.env.NODE_ENV === 'development' ||
    (userProfile?.username?.toLowerCase().startsWith('dscvr') ?? false) ||
    user?.email === 'dion@flowacademy.co.nz';

  const [isLoading, setIsLoading] = useState(true);
  const [turnCount, setTurnCount] = useState(0);
  const [playerMoveCount, setPlayerMoveCount] = useState(0); // Track player moves separately for solo tutorials
  const [hasFirstClickHappened, setHasFirstClickHappened] = useState(false);
  const [tutorialCompleted, setTutorialCompleted] = useState<'regular' | 'secret' | 'loss' | null>(null);
  const [dynamicLossMessage, setDynamicLossMessage] = useState<string | null>(null);
  const [activeLossEffect, setActiveLossEffect] = useState<{type: LossType, position: {row: number, col: number}} | null>(null);
  const [suppressTutorialHighlights, setSuppressTutorialHighlights] = useState<boolean>(false);
  const [showTutorialIntro, setShowTutorialIntro] = useState<boolean>(false);
  const [isNavigating, setIsNavigating] = useState<boolean>(false);

  // Use global theme context instead of local dark mode state
  const { theme } = useTheme();
  const darkMode = theme === 'dark';

  // Log theme changes for debugging
  useEffect(() => {
  }, [theme, darkMode]);

  // Mobile detection
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Clear the AI plan panel whenever the tutorial URL changes (next/prev/back
  // navigation). The previous plan referenced pieces and positions that no
  // longer match the new tutorial's board.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('tutorialAIPlanClear'));
  }, [constellation, tutorialIndex]);
  
  // Debug wrapper for setTutorialCompleted to track all state changes
  //
  // ...and the one funnel every tutorial ending passes through, win or lose, so
  // it's where the ending cue belongs (see UISoundEffects). The ref guards the
  // sound only: this is called on every reset too, and repeat calls with the
  // same result must not stack a second phrase on top of the first.
  const endCueRef = useRef<'regular' | 'secret' | 'loss' | null>(null);
  const setTutorialCompletedDebug = (newValue: 'regular' | 'secret' | 'loss' | null) => {
    if (newValue === null) {
      endCueRef.current = null;
    } else if (endCueRef.current !== newValue) {
      endCueRef.current = newValue;
      if (newValue === 'loss') playDefeatSound();
      else playVictorySound();
    }
    setTutorialCompleted(newValue);
  };

  // Enhanced delayed loss function with visual effects and sound
  const setTutorialLossWithDelay = (
    delayMs: number = 1000,
    failureReason: string = 'Tutorial failed',
    effectPosition?: { row: number; col: number }
  ) => {
    // DEBUGGING: Log loss function call
    
    // Debug logging for Tree tutorial issues (can be removed once stable)
    if (constellation === 'Tree' && tutorialIndex === '3') {
    }
    
    // AGGRESSIVE GUARD: Stop all infinite loops immediately
    if (tutorialCompleted !== null || (window as any).tutorialLossInProgress || isNavigating) {
      return;
    }
    
    // SAFETY: Block if still loading tutorial completely
    if (isLoading) {
      return;
    }
    
    // SAFETY: Block if no moves have been made yet
    if (!gameState || gameState.turnCount <= 0) {
      return;
    }
    
    // SAFETY: Block if called with outdated tutorial info
    if (!constellation || !tutorialIndex) {
      return;
    }
    
    // Set global flag to prevent multiple calls
    (window as any).tutorialLossInProgress = true;
    
    
    // CRITICAL: Immediately set the tutorialFailed flag to prevent AI from making more moves
    const currentGameState = ZodiacEngineService.zodiacEngine.getGameState();
    (currentGameState as any).tutorialFailed = true;
    
    // Determine loss type from failure reason
    const lossType = constellation && tutorialIndex ?
      TutorialSoundEffects.getLossTypeFromFailure(
        constellation,
        parseInt(tutorialIndex) - 1,
        failureReason
      ) :
      LossType.GENERAL;
    
    // Sound effects disabled to prevent audio errors
    
    // Use the specified effect position or derive it generically
    let visualEffectPosition = effectPosition;

    if (!visualEffectPosition && constellation && tutorialIndex) {
      const tutorialIndexNum = parseInt(tutorialIndex) - 1;
      const currentGameState = ZodiacEngineService.zodiacEngine.getGameState();

      // Check for monkey gamble failure position first - ONLY for Monkey tutorials
      if (constellation === 'Monkey' && (currentGameState as any).monkeyGambleLossEffectPosition) {
        const monkeyGamblePosition = (currentGameState as any).monkeyGambleLossEffectPosition;
        visualEffectPosition = { ...monkeyGamblePosition };
      }

      // Try to use engine's lastMove info for precise square
      const lastMove = (currentGameState as any).lastMove as
        | {
            fromPosition: { row: number; col: number };
            toPosition: { row: number; col: number };
            mover: { type: string; isLight: boolean };
            moveType: any;
            captureEffects: Array<{
              position: { row: number; col: number };
              capturedPieces: Array<{ type: string; isLight: boolean }>;
            }>;
          }
        | undefined;

      // Helper: find light king type and dark king type from game state
      const lightKingType = (currentGameState as any).lightKing;
      const darkKingType = (currentGameState as any).darkKing;

      // 1) If a capture happened, prefer the capture square
      if (lastMove && lastMove.captureEffects && lastMove.captureEffects.length > 0) {
        // If any captured piece was a light king (player king), show effect at capture position
        const kingCaptureEffect = lastMove.captureEffects.find((ce) =>
          ce.capturedPieces?.some(
            (p) => p.isLight === true && (p as any).type === lightKingType
          )
        );
        if (kingCaptureEffect) {
          visualEffectPosition = { ...kingCaptureEffect.position };
        }
        // If not a king, but some light piece was captured that triggered loss, still show capture square
        if (!visualEffectPosition) {
          const anyLightCaptureEffect = lastMove.captureEffects.find((ce) =>
            ce.capturedPieces?.some((p) => p.isLight === true)
          );
          if (anyLightCaptureEffect) {
            visualEffectPosition = { ...anyLightCaptureEffect.position };
          }
        }
      }

      // 2) If not a capture or not a king capture, check enemy race: dark king on row 0
      if (!visualEffectPosition && darkKingType) {
        for (let row = 0; row < currentGameState.board.length; row++) {
          for (let col = 0; col < currentGameState.board[row].length; col++) {
            const square = currentGameState.board[row][col];
            if (
              square.Pieces.some(
                (p: any) => !p.isLight && p.isKing && p.type === darkKingType
              )
            ) {
              // If this dark king reached row 0, effect is on this square
              if (row === 0) {
                visualEffectPosition = { row, col };
                break;
              }
            }
          }
          if (visualEffectPosition) break;
        }
      }

      // 3) If still not found, fallback to the mover's destination square if dark moved (likely culprit)
      if (!visualEffectPosition && lastMove && lastMove.mover && !lastMove.mover.isLight) {
        visualEffectPosition = { ...lastMove.toPosition };
      }

      // Final fallback
      if (!visualEffectPosition) {
        visualEffectPosition = { row: 2, col: 2 };
      }
    } else if (!visualEffectPosition) {
      visualEffectPosition = { row: 2, col: 2 };
    }
    
    // Skip visible effect for turn-limit/out-of-move losses
    const isTurnLimitLoss = /turn limit/i.test(failureReason) || /out of move/i.test(failureReason);

    if (!isTurnLimitLoss) {
      // Set the active loss effect for rendering and suppress other tutorial highlights
      setActiveLossEffect({ type: lossType, position: visualEffectPosition });
      setSuppressTutorialHighlights(true);
    } else {
    }
    
    // Set dynamic loss message for Bear tutorial 2
    if (constellation === 'Bear' && tutorialIndex === '2') {
      const currentGameStateForCheck = ZodiacEngineService.zodiacEngine.getGameState();
      const turnLimit = 6; // Bear tutorial 2 has 6 turn limit

      // Check if it's a turn limit loss
      if (/turn limit/i.test(failureReason) || /out of move/i.test(failureReason) || /out of turn/i.test(failureReason) ||
          (currentGameStateForCheck && currentGameStateForCheck.turnCount >= turnLimit)) {
        setDynamicLossMessage('You ran out of turns!');
      } else {
        // Check if player's bear was captured by looking at the board
        const lightBears = currentGameStateForCheck?.board?.flat()
          .flatMap((square: any) => square.Pieces)
          .filter((piece: any) => piece.type === 'Bear' && piece.isLight) || [];

        if (lightBears.length === 0 || /captured/i.test(failureReason)) {
          setDynamicLossMessage('You were captured by enemy bear!');
        }
      }
    }

    // Bear tutorial 5 (rhino challenge) — distinguish kingslayer vs race losses.
    // The default config message assumes the rhino king crossed the board, but
    // the same loss fires when the rhino king CAPTURES the bear king on row 0.
    if (constellation === 'Bear' && tutorialIndex === '5') {
      const stateForCheck = ZodiacEngineService.zodiacEngine.getGameState();
      const lightKingType = (stateForCheck as any)?.lightKing;
      const lastMoveInfo = (stateForCheck as any)?.lastMove;

      // Kingslayer takes priority: did the most recent move capture the player's king?
      const kingCapturedInLastMove = lastMoveInfo?.captureEffects?.some((ce: any) =>
        ce.capturedPieces?.some(
          (p: any) => p.isLight === true && p.type === lightKingType
        )
      );

      if (kingCapturedInLastMove) {
        setDynamicLossMessage('A dark rhino captured your bear king!');
      } else {
        const lightBearsLeft = stateForCheck?.board?.flat()
          .flatMap((square: any) => square.Pieces)
          .filter((p: any) => p && p.type === 'Bear' && p.isLight)?.length ?? 0;
        if (lightBearsLeft === 0) {
          setDynamicLossMessage('All your bears were captured!');
        }
        // Otherwise fall through to the config's "rhino reached your backrank" message.
      }
    }

    const lossTimeoutId = setTimeout(() => {
      // Clear the visual effect, restore highlights, and show popup
      setActiveLossEffect(null);
      setSuppressTutorialHighlights(false);
      setTutorialCompletedDebug('loss');
      // Clear the global flag to allow future loss effects
      (window as any).tutorialLossInProgress = false;
    }, delayMs);
    
    // Track this timeout so it can be cancelled if tutorial changes
    immediateFailureTimeoutsRef.current.push(lossTimeoutId);
  };

  // Track scheduled win checks to cancel them during initialization
  const scheduledWinCheckRef = useRef<NodeJS.Timeout | null>(null);
  
  // Track immediate failure detection timeouts to cancel them during tutorial changes
  const immediateFailureTimeoutsRef = useRef<NodeJS.Timeout[]>([]);
  
  // Track if victory has already been triggered to prevent multiple rapid notifications
  const victoryTriggeredRef = useRef<boolean>(false);

  // Comprehensive reset whenever constellation or tutorial changes
  useEffect(() => {
    
    // Get game state early for logging and cleanup
    const gameState = ZodiacEngineService.zodiacEngine.getGameState();
    
    // CRITICAL: Set loading to true immediately to block failure detection during transition
    setIsLoading(true);

    // Scrub stale victory state from the previous tutorial BEFORE the new
    // FEN load races against queued AI / win-check callbacks. loadTestScenarioFEN
    // also resets these later, but a stray check landing in the gap between
    // tutorial-change-detected and FEN-loaded would otherwise observe the
    // previous tutorial's PostGame + lightWon=true + tutorialConfig.
    if (gameState) {
      gameState.state = State.Playing;
      (gameState as any).lightWon = undefined;
      gameState.turnCount = 0;
      (gameState as any).tutorialConfig = undefined;
      (gameState as any).tutorialFailed = false;
      // eslint-disable-next-line no-console
      console.warn(`[TUT-DIAG] STATE-SCRUB tut=${constellation}/${tutorialIndex}`);
    }

    // CRITICAL: Cancel any scheduled win checks from previous tutorial
    if (scheduledWinCheckRef.current) {
      clearTimeout(scheduledWinCheckRef.current);
      scheduledWinCheckRef.current = null;
    }
    
    // CRITICAL: Cancel any immediate failure detection timeouts from previous tutorial
    immediateFailureTimeoutsRef.current.forEach(timeoutId => {
      clearTimeout(timeoutId);
    });
    immediateFailureTimeoutsRef.current = [];
    
    // Clear global loss flag on tutorial change
    (window as any).tutorialLossInProgress = false;
    
    // Reset victory triggered flag
    victoryTriggeredRef.current = false;
    
    // Clear monkey gamble failure data when changing tutorials
    if ((gameState as any).monkeyGambleLossEffectPosition) {
      (gameState as any).monkeyGambleLossEffectPosition = undefined;
    }
    if ((gameState as any).lastMonkeyGambleFailure) {
      (gameState as any).lastMonkeyGambleFailure = undefined;
    }
    
    setTutorialCompletedDebug(null);
    setDynamicLossMessage(null);
    setTurnCount(0);
    setPlayerMoveCount(0);
    setHasFirstClickHappened(false);
    setShowTutorialIntro(false); // Reset tutorial intro on tutorial change
    setIsNavigating(false); // Reset navigation flag on tutorial change
  }, [constellation, tutorialIndex]);

  // Use GameContext instead of local gameState
  const { gameState, updateGameState, potentialMoves, potentialSideEffects, selectedPiece } = useContext(GameContext)!;

  // No manual filtering needed - using Stone pieces in engine instead

  // No-op function - dev console has been removed but calls remain throughout
  const addToConsole = useCallback((_message: string, _type: 'info' | 'warning' | 'error' = 'info') => {}, []);

  // Track first piece selection for tutorial guidance
  useEffect(() => {
    if (!hasFirstClickHappened && selectedPiece) {
      // In bear0 tutorial, when a piece gets selected for the first time, hide the glow
      if (constellation === 'Bear' && tutorialIndex === '1') {
        setHasFirstClickHappened(true);
        addToConsole('🎯 First piece selected! Tutorial guidance complete.', 'info');
      }
    }
  }, [selectedPiece, hasFirstClickHappened, constellation, tutorialIndex]);


  // Track tutorialCompleted state changes (minimal logging)
  useEffect(() => {
    if (tutorialCompleted === 'loss') {
    }
  }, [tutorialCompleted]);

  // Check for tutorial win conditions
  const checkForWinCondition = useCallback((currentGameState?: any) => {
    const gameStateToUse = currentGameState || gameState;
    
    
    // Skip if tutorial already completed
    if (tutorialCompleted !== null) {
      return;
    }
    
    if (!constellation || !tutorialIndex || !gameStateToUse) {
      return;
    }
    
    // Don't check win conditions if we haven't made any moves yet
    if (gameStateToUse.turnCount === 0) {
      return;
    }

    // DISABLED: Duplicate failure detection - handled by dedicated failure detection
    // if (constellation && tutorialIndex) {
    //   const hasFailed = checkTutorialFailure(...);
    //   if (hasFailed) { setTutorialCompleted('loss'); }
    // }
    
    // Simple protection: Only skip if we're truly at the start with no moves made
    // This prevents ghost effects on tutorial load while allowing legitimate victories
    if (gameStateToUse.turnCount === 0 && turnCount === 0) {
      return;
    }
    
    // CRITICAL DEBUGGING: This should not be called if tutorial just restarted
    addToConsole(`🚨 WIN CHECK PROCEEDING: gameState.turnCount=${gameStateToUse.turnCount}`, 'warning');

    const isBear5 = constellation === 'Bear' && parseInt(tutorialIndex) - 1 === 4;
    if (isBear5) {
      // eslint-disable-next-line no-console
      console.warn(`[BEAR5-DIAG] ENTRY src=ui turn=${gameStateToUse.turnCount} propsConst=${constellation} propsIdx=${tutorialIndex}`);
      // eslint-disable-next-line no-console
      console.warn(`[BEAR5-DIAG] CENSUS src=ui turn=${gameStateToUse.turnCount} ${bear5DiagCensus(gameStateToUse.board)}`);
    }

    const isBear4 = constellation === 'Bear' && parseInt(tutorialIndex) - 1 === 3;
    if (isBear4) {
      // eslint-disable-next-line no-console
      console.warn(`[BEAR4-DIAG] ENTRY src=ui turn=${gameStateToUse.turnCount} propsConst=${constellation} propsIdx=${tutorialIndex}`);
      // eslint-disable-next-line no-console
      console.warn(`[BEAR4-DIAG] CENSUS src=ui turn=${gameStateToUse.turnCount} ${bear4DiagCensus(gameStateToUse.board)}`);
    }

    // Get all light pieces (player pieces) and check their positions
    const lightPieces = gameStateToUse.board.flat()
      .flatMap((square: any) => square.Pieces)
      .filter((piece: any) => piece.isLight);

    for (const piece of lightPieces) {
      // Find the piece's position
      for (let row = 0; row < gameStateToUse.board.length; row++) {
        for (let col = 0; col < gameStateToUse.board[row].length; col++) {
          const square = gameStateToUse.board[row][col];
          if (square.Pieces.includes(piece)) {
            const position = { row, col };

            const winType = checkTutorialWinCondition(
              constellation,
              parseInt(tutorialIndex) - 1,
              position,
              piece,
              gameStateToUse.turnCount,
              gameStateToUse.board
            );

            // Universal turn-1 secret-victory backstop. Single source of
            // truth lives in isSecretVictoryEligible (TutorialWinConditions).
            if (!isSecretVictoryEligible(
              winType,
              gameStateToUse.turnCount,
              'ui',
              `tut=${constellation}/${parseInt(tutorialIndex) - 1} trigger=${piece.type}@${row},${col}`
            )) {
              continue;
            }

            // Crane-1 regular-win backstop: light crane king starts at row 1
            // and moves max 2 sq, so a legitimate race takes ≥3 player moves.
            // Anything firing earlier is a snapshot/state bug — refuse to flip
            // PostGame and log loud diagnostics so we can trace the cause.
            if (winType === 'regular' &&
                constellation === 'Crane' &&
                parseInt(tutorialIndex) - 1 === 0 &&
                gameStateToUse.turnCount < 2) {
              // eslint-disable-next-line no-console
              console.warn(`[CRANE1-DIAG] REGULAR-BLOCKED src=ui turn=${gameStateToUse.turnCount} trigger=${piece.type}@${row},${col} isKing=${piece.isKing}`);
              continue;
            }

            if (winType && gameStateToUse.turnCount > 0) {
              // Live-state cross-check for `secret` wins. Walks the
              // secretWin tree (including `multiple`/`all_of` wrappers) and
              // re-evaluates EVERY leaf against the LIVE engine board.
              // Refuses to fire if the live-tree eval disagrees with the
              // snapshot-based `winType === 'secret'`. Catches stale
              // snapshots AND in-place mutations during AI search
              // (alchemist-mcts-state-isolation pattern). Handles both
              // `capture_target` and `reach_row` leaves — the previous
              // narrow check only collected `capture_target` and missed
              // Bear[3]'s mixed `multiple` (capture OR reach_row) wrap.
              if (winType === 'secret') {
                const cfg = (TUTORIAL_WIN_CONFIGS as any)[constellation]?.[parseInt(tutorialIndex) - 1];
                const liveBoard = ZodiacEngineService.zodiacEngine.getGameState()?.board || [];

                const evalLeaf = (node: any, board: any[][]): boolean => {
                  if (!node) return false;
                  if (node.type === 'capture_target' && node.target && node.targetSide) {
                    for (const r of board) {
                      if (!r) continue;
                      for (const sq of r) {
                        if (!sq?.Pieces) continue;
                        for (const p of sq.Pieces) {
                          if (!p) continue;
                          if (p.type === node.target &&
                              p.isLight === (node.targetSide === 'light') &&
                              (!node.requireKing || p.isKing === true)) {
                            return false;
                          }
                        }
                      }
                    }
                    return true;
                  }
                  if (node.type === 'reach_row' && node.target && node.targetSide && typeof node.row === 'number') {
                    const row = board[node.row];
                    if (!row) return false;
                    for (const sq of row) {
                      if (!sq?.Pieces) continue;
                      for (const p of sq.Pieces) {
                        if (!p) continue;
                        if (p.type === node.target &&
                            p.isLight === (node.targetSide === 'light') &&
                            (!node.requireKing || p.isKing === true)) {
                          return true;
                        }
                      }
                    }
                    return false;
                  }
                  return true;
                };

                const evalTree = (node: any, board: any[][]): boolean => {
                  if (!node) return false;
                  if (node.type === 'multiple' && Array.isArray(node.conditions)) {
                    return node.conditions.some((c: any) => evalTree(c, board));
                  }
                  if (node.type === 'all_of' && Array.isArray(node.conditions)) {
                    return node.conditions.every((c: any) => evalTree(c, board));
                  }
                  return evalLeaf(node, board);
                };

                const liveSaysSecret = evalTree(cfg?.secretWin, liveBoard) ||
                                       evalTree(cfg?.alternateSecretWin, liveBoard);
                if (!liveSaysSecret) {
                  // eslint-disable-next-line no-console
                  console.warn(`[TUT-DIAG] SECRET-LIVE-STATE-DISAGREES src=ui tut=${constellation}/${parseInt(tutorialIndex) - 1} turn=${gameStateToUse.turnCount} trigger=${piece.type}@${row},${col} — snapshot/check said secret but live-board tree-eval disagrees`);
                  continue;
                }
              }

              addToConsole(`🏆 VICTORY DETECTED: ${winType} at turn ${gameStateToUse.turnCount}`, 'info');

              if (isBear5 && winType === 'secret') {
                const stack = (new Error().stack || '').split('\n').slice(1, 7).join(' | ');
                // eslint-disable-next-line no-console
                console.warn(`[BEAR5-DIAG] SECRET-FIRED src=ui turn=${gameStateToUse.turnCount} trigger=${piece.type}@${row},${col} stack=${stack}`);
              }
              if (isBear4 && winType === 'secret') {
                const stack = (new Error().stack || '').split('\n').slice(1, 7).join(' | ');
                // eslint-disable-next-line no-console
                console.warn(`[BEAR4-DIAG] SECRET-FIRED src=ui turn=${gameStateToUse.turnCount} trigger=${piece.type}@${row},${col} stack=${stack}`);
              }

              // Prevent multiple victory notifications - check both state and ref
              if (tutorialCompleted === null && !victoryTriggeredRef.current) {
                victoryTriggeredRef.current = true; // Set immediately to block other callbacks
                setTutorialCompletedDebug(winType);
              } else {
                return;
              }

              if (winType === 'secret') {
                // Update constellation progress for secret victory
                const tutorialIndexNum = parseInt(tutorialIndex) - 1;
                const progressKey = constellation === 'Fungi' ? 'Mushroom' : constellation;
                const success = TutorialProgressService.completeTutorial(progressKey, tutorialIndexNum, winType);

                addToConsole(`🌟 SECRET VICTORY! ${piece.type} reached row ${row}!`, 'info');
                addToConsole(`🎉 Amazing! You discovered the secret win condition!`, 'info');
                if (success) {
                  addToConsole(`✨ Constellation ${constellation} glows brighter with secret power!`, 'info');
                }
              } else if (winType === 'loss') {
                // Don't update progress for losses - just show failure message
                addToConsole(`💀 TUTORIAL FAILED! ${piece.type} did not reach the goal in time!`, 'error');
                addToConsole(`❌ Try again to complete the tutorial!`, 'error');
              } else {
                // Update constellation progress for regular victory
                const tutorialIndexNum = parseInt(tutorialIndex) - 1;
                const progressKey = constellation === 'Fungi' ? 'Mushroom' : constellation;
                const success = TutorialProgressService.completeTutorial(progressKey, tutorialIndexNum, winType);
                
                addToConsole(`🎉 VICTORY! Tutorial completed successfully!`, 'info');
                addToConsole(`✅ You completed the tutorial within the move limit!`, 'info');
                if (success) {
                  addToConsole(`⭐ Constellation ${constellation} grows brighter!`, 'info');
                }
              }
              return;
            }
          }
        }
      }
    }
  }, [constellation, tutorialIndex, tutorialCompleted]);

  // Create tutorial stalemate callback using clean handler (after checkForWinCondition is defined)
  const handleTutorialStalemate = useCallback((eventData: StalemateEventData) => {
    addToConsole(`🚨 STALEMATE: ${eventData.message}`, 'error');
    
    // Only process if this is the correct tutorial
    if (constellation && tutorialIndex && 
        eventData.constellation === constellation && 
        eventData.tutorialIndex === parseInt(tutorialIndex) - 1) {
      
      // CRITICAL FIX: Check for victory BEFORE handling stalemate
      // This handles cases where capturing an enemy piece causes stalemate but should trigger victory
      
      // Use the fresh game state from the stalemate event data
      const freshGameState = eventData.gameState;

      const isBear5Stale = constellation === 'Bear' && parseInt(tutorialIndex) - 1 === 4;

      // Check victory with fresh game state instead of potentially stale React state
      if (freshGameState && freshGameState.turnCount > 0) {

        if (isBear5Stale) {
          // eslint-disable-next-line no-console
          console.warn(`[BEAR5-DIAG] ENTRY src=stalemate turn=${freshGameState.turnCount} propsConst=${constellation} propsIdx=${tutorialIndex}`);
          // eslint-disable-next-line no-console
          console.warn(`[BEAR5-DIAG] CENSUS src=stalemate turn=${freshGameState.turnCount} ${bear5DiagCensus(freshGameState.board)}`);
        }

        // Get all light pieces (player pieces) and check their positions using fresh state
        const lightPieces = freshGameState.board.flat()
          .flatMap(square => square.Pieces)
          .filter(piece => piece.isLight);

        for (const piece of lightPieces) {
          // Find the piece's position in fresh board
          for (let row = 0; row < freshGameState.board.length; row++) {
            for (let col = 0; col < freshGameState.board[row].length; col++) {
              const square = freshGameState.board[row][col];
              if (square.Pieces.includes(piece)) {
                const position = { row, col };

                const winType = checkTutorialWinCondition(
                  constellation,
                  parseInt(tutorialIndex) - 1,
                  position,
                  piece,
                  freshGameState.turnCount,
                  freshGameState.board
                );

                if (winType === 'secret' && freshGameState.turnCount < 2) {
                  // eslint-disable-next-line no-console
                  console.warn(`[TUT-DIAG] SECRET-T1-BLOCKED src=stalemate tut=${constellation}/${parseInt(tutorialIndex) - 1} turn=${freshGameState.turnCount} trigger=${piece.type}@${row},${col}`);
                  continue;
                }

                // Crane-1 regular-win backstop (stalemate path): mirrors the
                // checkForWinCondition guard. Race takes ≥3 moves so any
                // sub-turn-2 regular win is a bug, not legitimate play.
                if (winType === 'regular' &&
                    constellation === 'Crane' &&
                    parseInt(tutorialIndex) - 1 === 0 &&
                    freshGameState.turnCount < 2) {
                  // eslint-disable-next-line no-console
                  console.warn(`[CRANE1-DIAG] REGULAR-BLOCKED src=stalemate turn=${freshGameState.turnCount} trigger=${piece.type}@${row},${col} isKing=${piece.isKing}`);
                  continue;
                }

                if (winType && winType !== 'loss' && freshGameState.turnCount > 0) {

                  // Same live-state cross-check as the main checkForWinCondition
                  // path. Re-evaluates the secretWin tree against the live
                  // engine board and refuses to fire if it disagrees with the
                  // snapshot-based result. See checkForWinCondition for the
                  // full rationale.
                  if (winType === 'secret') {
                    const cfg = (TUTORIAL_WIN_CONFIGS as any)[constellation]?.[parseInt(tutorialIndex) - 1];
                    const liveBoard = ZodiacEngineService.zodiacEngine.getGameState()?.board || [];
                    const evalLeaf = (node: any, board: any[][]): boolean => {
                      if (!node) return false;
                      if (node.type === 'capture_target' && node.target && node.targetSide) {
                        for (const r of board) {
                          if (!r) continue;
                          for (const sq of r) {
                            if (!sq?.Pieces) continue;
                            for (const p of sq.Pieces) {
                              if (!p) continue;
                              if (p.type === node.target &&
                                  p.isLight === (node.targetSide === 'light') &&
                                  (!node.requireKing || p.isKing === true)) {
                                return false;
                              }
                            }
                          }
                        }
                        return true;
                      }
                      if (node.type === 'reach_row' && node.target && node.targetSide && typeof node.row === 'number') {
                        const rrow = board[node.row];
                        if (!rrow) return false;
                        for (const sq of rrow) {
                          if (!sq?.Pieces) continue;
                          for (const p of sq.Pieces) {
                            if (!p) continue;
                            if (p.type === node.target &&
                                p.isLight === (node.targetSide === 'light') &&
                                (!node.requireKing || p.isKing === true)) {
                              return true;
                            }
                          }
                        }
                        return false;
                      }
                      return true;
                    };
                    const evalTree = (node: any, board: any[][]): boolean => {
                      if (!node) return false;
                      if (node.type === 'multiple' && Array.isArray(node.conditions)) {
                        return node.conditions.some((c: any) => evalTree(c, board));
                      }
                      if (node.type === 'all_of' && Array.isArray(node.conditions)) {
                        return node.conditions.every((c: any) => evalTree(c, board));
                      }
                      return evalLeaf(node, board);
                    };
                    const liveSaysSecret = evalTree(cfg?.secretWin, liveBoard) ||
                                           evalTree(cfg?.alternateSecretWin, liveBoard);
                    if (!liveSaysSecret) {
                      // eslint-disable-next-line no-console
                      console.warn(`[TUT-DIAG] SECRET-LIVE-STATE-DISAGREES src=stalemate tut=${constellation}/${parseInt(tutorialIndex) - 1} turn=${freshGameState.turnCount} trigger=${piece.type}@${row},${col} — snapshot/check said secret but live-board tree-eval disagrees`);
                      continue;
                    }
                  }

                  if (isBear5Stale && winType === 'secret') {
                    const stack = (new Error().stack || '').split('\n').slice(1, 7).join(' | ');
                    // eslint-disable-next-line no-console
                    console.warn(`[BEAR5-DIAG] SECRET-FIRED src=stalemate turn=${freshGameState.turnCount} trigger=${piece.type}@${row},${col} stack=${stack}`);
                  }

                  // Prevent multiple victory notifications - check both state and ref
                  if (tutorialCompleted === null && !victoryTriggeredRef.current) {
                    victoryTriggeredRef.current = true; // Set immediately to block other callbacks
                    setTutorialCompletedDebug(winType);
                  } else {
                  }
                  
                  // CRITICAL FIX: Update constellation progress for the victory
                  const tutorialIndexNum = parseInt(tutorialIndex) - 1;
                  const progressKey = constellation === 'Fungi' ? 'Mushroom' : constellation;
                  const success = TutorialProgressService.completeTutorial(progressKey, tutorialIndexNum, winType as 'regular' | 'secret');
                  
                  addToConsole(`🏆 VICTORY DETECTED: ${winType} at turn ${freshGameState.turnCount}`, 'info');
                  
                  if (winType === 'secret') {
                    addToConsole(`🌟 SECRET VICTORY! ${piece.type} captured the enemy piece!`, 'info');
                    addToConsole(`🎉 Amazing! You discovered the secret win condition!`, 'info');
                    if (success) {
                      addToConsole(`✨ Constellation ${constellation} glows brighter with secret power!`, 'info');
                    }
                  } else {
                    addToConsole(`🎉 VICTORY! Tutorial completed successfully!`, 'info');
                    addToConsole(`✅ You completed the tutorial objective!`, 'info');
                    if (success) {
                      addToConsole(`⭐ Constellation ${constellation} grows brighter!`, 'info');
                    }
                  }
                  
                  return; // Exit immediately on victory
                }
              }
            }
          }
        }
      } else {
        checkForWinCondition();
      }
      
      // Wait a moment for victory detection to complete, then check if tutorial is still active
      setTimeout(() => {
        if (tutorialCompleted !== null) {
          return;
        }
        
        // Use clean handler to determine if stalemate should trigger loss
        if (shouldStalemateTriggerLoss(eventData)) {
          const lossMessage = getStalemateLossMessage(eventData);
          addToConsole(`💀 ${lossMessage}`, 'error');
          setTutorialLossWithDelay(1000, lossMessage);
        } else {
          addToConsole(`ℹ️ Stalemate handled by tutorial-specific logic`, 'info');
        }
      }, 100); // 100ms delay to allow victory detection to complete
    }
  }, [constellation, tutorialIndex, tutorialCompleted, setTutorialCompletedDebug]);

  // Tutorial unlock order - matches the original game progression
  const TUTORIAL_UNLOCK_ORDER = [
    'Bear', 'Crane', 'Tree', 'Monkey', 'Wolf', 'Rhino', 
    'Snake', 'Fungi', 'Penguin', 'Frog', 'Goat', 'Octopus', 'Virus'
  ];

  // Tutorial counts for each constellation (matching TutorialProgressService)
  const TUTORIAL_COUNTS: { [key: string]: number } = {
    'Bear': 5,
    'Crane': 2, 
    'Tree': 3,
    'Monkey': 5,
    'Wolf': 3,
    'Rhino': 5,
    'Snake': 4,
    'Fungi': 4, // Note: stored as 'Mushroom' in TutorialProgressService but 'Fungi' everywhere else
    'Penguin': 4,
    'Frog': 5,
    'Goat': 4,
    'Octopus': 6,
    'Virus': 3 // Special case - not in unlock sequence but has tutorials
  };

  // Get current tutorial position in the unlock sequence
  const getCurrentTutorialPosition = useCallback(() => {
    if (!constellation || !tutorialIndex) return null;
    
    const constellationIndex = TUTORIAL_UNLOCK_ORDER.indexOf(constellation);
    if (constellationIndex === -1) return null;
    
    const tutorialNum = parseInt(tutorialIndex) - 1; // Convert to 0-based
    
    return { constellationIndex, tutorialNum };
  }, [constellation, tutorialIndex]);

  // Navigate to next tutorial
  const goToNextTutorial = useCallback(() => {
    // Clear FEN history to prevent draw data persisting between tutorials
    const gameState = ZodiacEngineService.zodiacEngine.getGameState();
    if (gameState) {
      ZodiacEngineService.zodiacEngine.fenHistoryManager.clearHistory(gameState);
    }

    const currentPos = getCurrentTutorialPosition();
    if (!currentPos) return;

    const { constellationIndex, tutorialNum } = currentPos;
    const currentConstellation = TUTORIAL_UNLOCK_ORDER[constellationIndex];
    const maxTutorials = TUTORIAL_COUNTS[currentConstellation] || 0;
    
    // Check if we can go to next tutorial in same constellation
    if (tutorialNum + 1 < maxTutorials) {
      navigate(`/tutorials/${currentConstellation}/${tutorialNum + 2}`);
    } else {
      // Move to first tutorial of next constellation (if unlocked)
      const nextConstellationIndex = constellationIndex + 1;
      if (nextConstellationIndex < TUTORIAL_UNLOCK_ORDER.length) {
        const nextConstellation = TUTORIAL_UNLOCK_ORDER[nextConstellationIndex];
        // Check if next constellation is unlocked
        const progressKey = nextConstellation === 'Fungi' ? 'Mushroom' : nextConstellation;
        const progress = TutorialProgressService.getConstellationProgress(progressKey);
        if (progress?.isUnlocked) {
          navigate(`/tutorials/${nextConstellation}/1`);
        }
      }
    }
  }, [getCurrentTutorialPosition, navigate]);

  // Navigate to previous tutorial
  const goToPreviousTutorial = useCallback(() => {
    // Clear FEN history to prevent draw data persisting between tutorials
    const gameState = ZodiacEngineService.zodiacEngine.getGameState();
    if (gameState) {
      ZodiacEngineService.zodiacEngine.fenHistoryManager.clearHistory(gameState);
    }

    const currentPos = getCurrentTutorialPosition();
    if (!currentPos) return;

    const { constellationIndex, tutorialNum } = currentPos;
    
    // Check if we can go to previous tutorial in same constellation
    if (tutorialNum > 0) {
      const currentConstellation = TUTORIAL_UNLOCK_ORDER[constellationIndex];
      navigate(`/tutorials/${currentConstellation}/${tutorialNum}`);
    } else {
      // Move to last tutorial of previous constellation
      const prevConstellationIndex = constellationIndex - 1;
      if (prevConstellationIndex >= 0) {
        const prevConstellation = TUTORIAL_UNLOCK_ORDER[prevConstellationIndex];
        const maxTutorials = TUTORIAL_COUNTS[prevConstellation] || 0;
        navigate(`/tutorials/${prevConstellation}/${maxTutorials}`);
      }
    }
  }, [getCurrentTutorialPosition, navigate]);

  // Check if navigation buttons should be enabled
  const canGoNext = useCallback(() => {
    const currentPos = getCurrentTutorialPosition();
    if (!currentPos) return false;
    
    const { constellationIndex, tutorialNum } = currentPos;
    const currentConstellation = TUTORIAL_UNLOCK_ORDER[constellationIndex];
    const maxTutorials = TUTORIAL_COUNTS[currentConstellation] || 0;
    
    // Check if we can go to next tutorial in same constellation
    if (tutorialNum + 1 < maxTutorials) {
      return true;
    }
    
    // Check if we can go to next constellation (if it's unlocked)
    const nextConstellationIndex = constellationIndex + 1;
    if (nextConstellationIndex < TUTORIAL_UNLOCK_ORDER.length) {
      const nextConstellation = TUTORIAL_UNLOCK_ORDER[nextConstellationIndex];
      const progressKey = nextConstellation === 'Fungi' ? 'Mushroom' : nextConstellation;
      const progress = TutorialProgressService.getConstellationProgress(progressKey);
      return progress?.isUnlocked || false;
    }
    
    return false;
  }, [getCurrentTutorialPosition]);

  const canGoPrevious = useCallback(() => {
    const currentPos = getCurrentTutorialPosition();
    if (!currentPos) return false;
    
    const { constellationIndex, tutorialNum } = currentPos;
    
    // Can go previous if not on first tutorial of first constellation
    return !(constellationIndex === 0 && tutorialNum === 0);
  }, [getCurrentTutorialPosition]);

  // Extract terrain application logic into a separate function
  const applyTerrainToBoard = useCallback(() => {
    if (!constellation || !tutorialIndex) return;
    
    // Get terrain data for this tutorial
    const tutorialIndexNum = parseInt(tutorialIndex) - 1;
    const terrainData = getTutorialTerrain(constellation, tutorialIndexNum);
    
    // Get the CSS module class name for squares
    const squareClassName = globalStyles.square;
    const squares = document.querySelectorAll(`.${squareClassName}`);
    
    if (squares.length === 0) return;
    
    squares.forEach((square, index) => {
      const element = square as HTMLElement;
      const row = Math.floor(index / 5);
      const col = index % 5;
      
      // Get terrain type for this square (default to checkerboard if not found)
      const terrainType = terrainData[row]?.[col] ?? (row + col) % 2;
      
      // Skip background terrain if this position will have overlay terrain
      if (shouldTerrainRenderAsOverlay(constellation, tutorialIndexNum, terrainType)) {
        // Use checkerboard background instead of the rock texture
        const checkerboardType = (row + col) % 2;
        const checkerboardPath = getTerrainImagePath(checkerboardType);
        element.style.backgroundImage = `url('${checkerboardPath}')`;
      } else {
        // Apply normal terrain styling
        const terrainImagePath = getTerrainImagePath(terrainType);
        element.style.backgroundImage = `url('${terrainImagePath}')`;
      }
      
      element.style.backgroundSize = "cover";
      element.style.backgroundPosition = "center";
      element.style.backgroundRepeat = "no-repeat";
    });
  }, [constellation, tutorialIndex]);

  const restartTutorial = useCallback(() => {
    if (!constellation || !tutorialIndex) return;

    // Clear global loss flag on restart
    (window as any).tutorialLossInProgress = false;

    // Clear stale AI plan from the previous attempt — it referenced moves on
    // the now-reset board state.
    window.dispatchEvent(new CustomEvent('tutorialAIPlanClear'));

    // CRITICAL: Clear engine tutorialFailed flag on the REAL engine state (not a shallow copy)
    // engine.getGameState() returns shallow copies, so we must access gameStateManager directly
    const realState = (ZodiacEngineService.zodiacEngine as any).gameStateManager.getGameState();
    if (realState) {
      (realState as any).tutorialFailed = false;
    }

    try {
      const tutorialFEN = getTutorialFEN(constellation, parseInt(tutorialIndex) - 1);
      addToConsole(`Restarting ${constellation} tutorial ${tutorialIndex}`, 'info');

      // Load the FEN scenario into the engine
      loadTestScenarioFEN(ZodiacEngineService.zodiacEngine, tutorialFEN);
      
      // Apply post-processing modifications if they exist
      const postProcessing = getTutorialPostProcessing(constellation, parseInt(tutorialIndex) - 1);
      if (postProcessing) {
        const gameState = ZodiacEngineService.zodiacEngine.getGameState();
        applyTutorialPostProcessing(gameState, postProcessing);
        addToConsole(`🔧 Applied post-processing modifications`, 'info');
      }
      
      // Reset turn count and tutorialFailed on the REAL engine state (not shallow copy)
      const realGameState = (ZodiacEngineService.zodiacEngine as any).gameStateManager.getGameState();
      realGameState.turnCount = 0;
      (realGameState as any).tutorialFailed = false;
      addToConsole(`🔄 Restart: Reset turnCount to 0 and tutorialFailed to false on real engine state`, 'info');

      // Log FEN history from REAL state (loadTestScenarioFEN now handles the reset)
      realGameState.fenHistory.forEach((fen: string, index: number) => {
      });
      
      // Check if this is a solo tutorial and configure the engine accordingly
      const isSolo = isTutorialSolo(constellation, parseInt(tutorialIndex) - 1);
      addToConsole(`🔧 Restart config check: constellation=${constellation}, index=${parseInt(tutorialIndex) - 1}, isSolo=${isSolo}`, 'info');
      
      // CRITICAL: Set appropriate mode based on whether this is a solo tutorial
      const gameMode = isSolo ? Mode.Tutorial : Mode.TestAI;
      ZodiacEngineService.zodiacEngine.setGameMode(gameMode);
      addToConsole(`🎓 Restart: Set game mode to ${gameMode} (isSolo=${isSolo}) to ${isSolo ? 'prevent AI moves' : 'allow AI moves'}`, 'info');
      
      const restartTurnLimit = getTutorialTurnLimit(constellation, parseInt(tutorialIndex) - 1);
      
      // Determine if this tutorial is missing kings and needs engine protection
      const tempState = ZodiacEngineService.zodiacEngine.getGameState();
      const hasLightKingType = !!(tempState as any).lightKing;
      const hasDarkKingType = !!(tempState as any).darkKing;
      const hasAnyLightKingPiece = tempState.board.some(row => row.some(sq => sq.Pieces?.some(p => p.isLight && p.isKing)));
      const hasAnyDarkKingPiece = tempState.board.some(row => row.some(sq => sq.Pieces?.some(p => !p.isLight && p.isKing)));
      
      // Check tutorial-specific king overrides
      const tutorialIndexNum = parseInt(tutorialIndex) - 1;
      const noDarkKingOverride = tutorialHasNoDarkKingOverride(constellation, tutorialIndexNum);
      const noLightKingOverride = tutorialHasNoLightKingOverride(constellation, tutorialIndexNum);
      
      // Apply overrides: ignore missing kings if tutorial explicitly allows it
      const effectiveHasDarkKing = noDarkKingOverride ? true : (hasDarkKingType && hasAnyDarkKingPiece);
      const effectiveHasLightKing = noLightKingOverride ? true : (hasLightKingType && hasAnyLightKingPiece);
      const needsKingProtection = !(hasLightKingType && effectiveHasDarkKing && effectiveHasLightKing && hasAnyLightKingPiece);

      // Debug logging for king override system
      if (noDarkKingOverride || noLightKingOverride) {
        addToConsole(`🔧 KING OVERRIDE: ${constellation} tutorial ${tutorialIndexNum + 1}`, 'info');
        addToConsole(`  - noDarkKing: ${noDarkKingOverride}, noLightKing: ${noLightKingOverride}`, 'info');
        addToConsole(`  - effectiveHasDarkKing: ${effectiveHasDarkKing}, effectiveHasLightKing: ${effectiveHasLightKing}`, 'info');
        addToConsole(`  - needsKingProtection: ${needsKingProtection}`, 'info');
      }

      // Engine-only protection: map to a protected tutorial that ignores missing kings (Tree tutorial 2)
      // EXCEPTION: Crane tutorials should never be redirected to Tree tutorial 2 to avoid using wrong AI
      const engineConstellation = (needsKingProtection && constellation !== 'Crane') ? 'Tree' : constellation;
      const engineTutorialIndex = (needsKingProtection && constellation !== 'Crane') ? 1 : (parseInt(tutorialIndex) - 1);

      ZodiacEngineService.zodiacEngine.setTutorialConfig({
        soloMode: isSolo,
        turnLimit: restartTurnLimit,
        // Engine protection mapping
        constellation: engineConstellation,
        tutorialIndex: engineTutorialIndex,
        // Preserve real UI info for reference
        realConstellation: constellation,
        realTutorialIndex: parseInt(tutorialIndex) - 1,
        uiLogger: addToConsole,
        stalemateCallback: handleTutorialStalemate
      });
      addToConsole(
        `${isSolo ? '✅ Solo' : 'ℹ️ Regular'} tutorial mode on restart - turn limit: ${restartTurnLimit}${needsKingProtection ? ' (engine king-protection enabled)' : ''}`,
        'info'
      );
      
      // Reset states
      setTurnCount(0);
      setPlayerMoveCount(0);
      setHasFirstClickHappened(false);
      setTutorialCompletedDebug(null);
      // Clear loss effects
      setActiveLossEffect(null);
      setSuppressTutorialHighlights(false);
      
      // Update game state - use shallow copy for React state, but ensure it has clean values
      setTimeout(() => {
        const updatedGameState = ZodiacEngineService.zodiacEngine.getGameState();
        // Ensure React state has correct values (shallow copy for React rendering)
        updatedGameState.turnCount = 0;
        (updatedGameState as any).tutorialFailed = false;

        // FEN history is already reset by loadTestScenarioFEN on the real engine state

        // Reset victory triggered flag on tutorial restart
        victoryTriggeredRef.current = false;

        // CRITICAL FIX: Explicitly restart the AIScheduler to ensure it's in the right mode
        const aiScheduler = ZodiacEngineService.zodiacEngine.aiScheduler;
        if (aiScheduler) {
          aiScheduler.startPlayVsAIMode(10);
        }

        updateGameState(updatedGameState);
        addToConsole('Tutorial restarted successfully', 'info');
      }, 50);
    } catch (error) {
      addToConsole(`Failed to restart tutorial: ${error}`, 'error');
    }
  }, [constellation, tutorialIndex, updateGameState]);

  useEffect(() => {
    if (!constellation || !tutorialIndex) {
      navigate('/tutorials');
      return;
    }

    // Close any existing victory popups when entering a new tutorial
    
    // CRITICAL: Cancel any scheduled win checks from previous tutorial
    if (scheduledWinCheckRef.current) {
      clearTimeout(scheduledWinCheckRef.current);
      scheduledWinCheckRef.current = null;
    }
    
    setTutorialCompletedDebug(null);

    // Initialize the game engine for tutorial mode
    const initializeTutorial = async () => {
      try {
        // Get the specific FEN for this tutorial
        const tutorialIndexNum = parseInt(tutorialIndex) - 1;
        const tutorialFEN = getTutorialFEN(constellation, tutorialIndexNum);
        addToConsole(`Loading ${constellation} tutorial ${tutorialIndex} (index ${tutorialIndexNum}) with FEN: ${tutorialFEN}`, 'info');
        
        // Load the FEN scenario into the engine
        loadTestScenarioFEN(ZodiacEngineService.zodiacEngine, tutorialFEN);
        
        // Apply post-processing modifications if they exist
        const postProcessing = getTutorialPostProcessing(constellation, tutorialIndexNum);
        if (postProcessing) {
          const gameState = ZodiacEngineService.zodiacEngine.getGameState();
          applyTutorialPostProcessing(gameState, postProcessing);
          addToConsole(`🔧 Applied post-processing modifications`, 'info');
        }
        
        // CRITICAL: Reset turn count to 0 after FEN loading
        ZodiacEngineService.zodiacEngine.resetTurnCount();
        addToConsole(`🔄 Reset turnCount to 0 after FEN loading`, 'info');
        
        // Check if this is a solo tutorial and configure the engine accordingly
        const isSolo = isTutorialSolo(constellation, tutorialIndexNum);
        addToConsole(`🔧 Tutorial config check: constellation=${constellation}, index=${tutorialIndexNum}, isSolo=${isSolo}`, 'info');
        
        // CRITICAL: Set appropriate mode based on whether this is a solo tutorial
        const gameMode = isSolo ? Mode.Tutorial : Mode.TestAI;
        ZodiacEngineService.zodiacEngine.setGameMode(gameMode);
        addToConsole(`🎓 Set game mode to ${gameMode} (isSolo=${isSolo}) to ${isSolo ? 'prevent AI moves' : 'allow AI moves'}`, 'info');
        
        // Determine if this tutorial is missing kings and needs engine protection
        const tempState = ZodiacEngineService.zodiacEngine.getGameState();
        const hasLightKingType = !!(tempState as any).lightKing;
        const hasDarkKingType = !!(tempState as any).darkKing;
        const hasAnyLightKingPiece = tempState.board.some(row => row.some(sq => sq.Pieces?.some(p => p.isLight && p.isKing)));
        const hasAnyDarkKingPiece = tempState.board.some(row => row.some(sq => sq.Pieces?.some(p => !p.isLight && p.isKing)));
        
        // Check tutorial-specific king overrides
        const noDarkKingOverride = tutorialHasNoDarkKingOverride(constellation, tutorialIndexNum);
        const noLightKingOverride = tutorialHasNoLightKingOverride(constellation, tutorialIndexNum);
        
        // Apply overrides: ignore missing kings if tutorial explicitly allows it
        const effectiveHasDarkKing = noDarkKingOverride ? true : (hasDarkKingType && hasAnyDarkKingPiece);
        const effectiveHasLightKing = noLightKingOverride ? true : (hasLightKingType && hasAnyLightKingPiece);
        const needsKingProtection = !(hasLightKingType && effectiveHasDarkKing && effectiveHasLightKing && hasAnyLightKingPiece);

        // Debug logging for king override system
        if (noDarkKingOverride || noLightKingOverride) {
          addToConsole(`🔧 KING OVERRIDE INIT: ${constellation} tutorial ${tutorialIndexNum + 1}`, 'info');
          addToConsole(`  - noDarkKing: ${noDarkKingOverride}, noLightKing: ${noLightKingOverride}`, 'info');
          addToConsole(`  - effectiveHasDarkKing: ${effectiveHasDarkKing}, effectiveHasLightKing: ${effectiveHasLightKing}`, 'info');
          addToConsole(`  - needsKingProtection: ${needsKingProtection}`, 'info');
        }

        // Engine-only protection: map to a protected tutorial that ignores missing kings (Tree tutorial 2)
        // EXCEPTION: Crane tutorials should never be redirected to Tree tutorial 2 to avoid using wrong AI
        const engineConstellation = (needsKingProtection && constellation !== 'Crane') ? 'Tree' : constellation;
        const engineTutorialIndex = (needsKingProtection && constellation !== 'Crane') ? 1 : tutorialIndexNum;

        ZodiacEngineService.zodiacEngine.setTutorialConfig({
          soloMode: isSolo,
          turnLimit: turnLimit,
          // Engine protection mapping
          constellation: engineConstellation,
          tutorialIndex: engineTutorialIndex,
          // Preserve real UI info for reference
          realConstellation: constellation,
          realTutorialIndex: tutorialIndexNum,
          uiLogger: addToConsole,
          stalemateCallback: handleTutorialStalemate
        });
        addToConsole(
          `${isSolo ? '✅ Solo' : 'ℹ️ Regular'} tutorial mode - turn limit: ${turnLimit}${needsKingProtection ? ' (engine king-protection enabled)' : ''}`,
          'info'
        );
        
        // Get the updated game state with a small delay to ensure FEN is processed
        setTimeout(() => {
          const updatedGameState = ZodiacEngineService.zodiacEngine.getGameState();
          const pieceCount = updatedGameState.board.reduce((total, row) => 
            total + row.reduce((rowTotal, square) => rowTotal + square.Pieces.length, 0), 0
          );
          addToConsole(`Tutorial loaded successfully - ${pieceCount} pieces on board`, 'info');
          addToConsole(`Current player: ${updatedGameState.isLightTurn ? 'Light' : 'Dark'}`, 'info');
          
          // Verify tutorialConfig persists in the updated game state
          const persistedConfig = (updatedGameState as any).tutorialConfig;
          addToConsole(`🔍 Post-load config check: tutorialConfig=${JSON.stringify(persistedConfig)}`, 'info');
          
          // CRITICAL DEBUG: Log turn count before and after updating game state
          
          // CRITICAL FIX: Ensure turnCount is 0 before updating React state
          // This prevents failure detection from triggering during initialization
          updatedGameState.turnCount = 0;
          
          // Clear any existing tutorial failure flags from previous games
          (updatedGameState as any).tutorialFailed = false;
          
          updateGameState(updatedGameState);
          setTurnCount(0); // Reset turn counter when tutorial loads
          setPlayerMoveCount(0); // Reset player move counter when tutorial loads
          setHasFirstClickHappened(false); // Reset first click status when tutorial loads
          setTutorialCompletedDebug(null); // Reset completion status when tutorial loads
          
          addToConsole(`🔄 INITIALIZATION COMPLETE: All states reset for fresh tutorial start`, 'info');
          
          // Apply terrain immediately after game state is updated
          setTimeout(() => {
            applyTerrainToBoard();
          }, 100);
          
          // Show tutorial intro popup after a brief delay to ensure everything is loaded
          setTimeout(() => {
            // Check if tutorial intro has been seen or disabled BEFORE showing it
            const tutorialKey = `${constellation}-${tutorialIndex}`;
            const seenKey = `tutorialIntroSeen-${tutorialKey}`;
            const disabledKey = 'tutorialIntroDisabled';

            const hasBeenSeen = localStorage.getItem(seenKey) === 'true';
            const isDisabled = localStorage.getItem(disabledKey) === 'true';

            if (!hasBeenSeen && !isDisabled) {
              setShowTutorialIntro(true);
            }
          }, 200);
        }, 50);
        
        setIsLoading(false);
      } catch (error) {
        addToConsole(`Failed to initialize tutorial: ${error}`, 'error');
        navigate('/tutorials');
      }
    };

    initializeTutorial();
  }, [constellation, tutorialIndex, navigate]);

  const turnLimit = constellation && tutorialIndex 
    ? getTutorialTurnLimit(constellation, parseInt(tutorialIndex) - 1)
    : 'unlimited';

  const isSoloTutorial = constellation && tutorialIndex
    ? isTutorialSolo(constellation, parseInt(tutorialIndex) - 1)
    : false;

  // Show AI thinking modal when it's dark's turn in non-solo tutorials (hide when tutorial ends)
  const showAIThinking = !isSoloTutorial && !tutorialCompleted && gameState?.state === State.Playing && !gameState?.isLightTurn;

  // Track moves for turn counter  
  useEffect(() => {
    if (!gameState) return;
    
    // Monitor game state changes for debugging (but don't interfere with game engine)
    
    // Log game state changes to UI console
    if (gameState.turnCount !== turnCount) {
      addToConsole(`📈 Turn count changed: ${turnCount} → ${gameState.turnCount}`, 'info');
      
      // CRITICAL: Don't check win conditions during initialization or if already completed
      if (tutorialCompleted !== null) {
        addToConsole(`🛑 Win check skipped: tutorial already completed`, 'info');
        setTurnCount(gameState.turnCount); // Still update the counter
        return;
      }
      
      if (isSoloTutorial) {
        // In solo tutorials, increment player move count on light player turns
        if (gameState.isLightTurn && gameState.turnCount > turnCount) {
          const newMoveCount = playerMoveCount + 1;
          setPlayerMoveCount(newMoveCount);
          addToConsole(`✅ Player move incremented: ${playerMoveCount} → ${newMoveCount}`, 'info');
          
          // Schedule win condition check after player move
          addToConsole(`🎯 SCHEDULING WIN CHECK: Solo tutorial move completed`, 'info');
          scheduledWinCheckRef.current = setTimeout(() => {
            // Get fresh game state directly from engine to avoid stale React state
            const freshGameState = ZodiacEngineService.zodiacEngine.getGameState();
            checkForWinCondition(freshGameState);
          }, 100);
          
          // Check if we've reached the turn limit
          if (turnLimit !== 'unlimited' && gameState.turnCount >= turnLimit) {
            addToConsole(`🚫 Turn limit reached (${gameState.turnCount}/${turnLimit}) - No more moves available!`, 'error');
            
            // DISABLED: Duplicate failure detection - handled by dedicated failure detection
            // if (constellation && tutorialIndex) {
            //   const hasFailed = checkTutorialFailure(...);
            //   if (hasFailed) { setTutorialCompleted('loss'); }
            // }
          } else if (turnLimit !== 'unlimited' && gameState.turnCount >= turnLimit - 1) {
            addToConsole(`Warning: Approaching move limit (${turnLimit})`, 'warning');
          }
        } else {
          addToConsole(`❌ Solo tutorial conditions not met`, 'warning');
        }
      } else {
        // In regular tutorials, track game turns
        addToConsole(`Turn ${gameState.turnCount}: ${gameState.isLightTurn ? 'Light' : 'Dark'} player's turn`, 'info');
        
        // CRITICAL FIX: Force board state update after AI (Dark) turns
        if (gameState.isLightTurn) {
          // Light player's turn - log FEN and schedule win condition check
          const lightFEN = ZodiacFEN.encode(gameState);
          addToConsole(`🎯 FEN after Light turn ${gameState.turnCount}: ${lightFEN}`, 'info');
          
          scheduledWinCheckRef.current = setTimeout(() => {
            // Get fresh game state directly from engine to avoid stale React state
            const freshGameState = ZodiacEngineService.zodiacEngine.getGameState();
            checkForWinCondition(freshGameState);
          }, 100);
        } else {
          // Dark player's turn just completed - force immediate board state refresh
          addToConsole(`🔄 Dark turn completed - forcing board state refresh`, 'info');
          setTimeout(() => {
            const freshGameState = ZodiacEngineService.zodiacEngine.getGameState();
            
            // CRITICAL: Log FEN after Dark move to track board state updates
            const currentFEN = ZodiacFEN.encode(freshGameState);
            addToConsole(`🎯 FEN after Dark turn ${freshGameState.turnCount}: ${currentFEN}`, 'info');
            
            updateGameState(freshGameState);
            addToConsole(`🔄 Board state refreshed after Dark turn`, 'info');
            
            // UNIVERSAL: Immediate failure check after Dark turn for ALL tutorials with immediate failure detection
            if (constellation && tutorialIndex && tutorialCompleted === null) {
              const tutorialIndexNum = parseInt(tutorialIndex) - 1;
              
              // Check if this tutorial needs immediate failure detection
              const needsImmediateCheck = (
                (constellation === "Bear" && tutorialIndexNum === 1) ||  // Bear Tutorial 2 (How to Race)
                (constellation === "Bear" && tutorialIndexNum === 2) ||  // Bear Tutorial 3 (Predator) — checks dark goat reaching row 0
                (constellation === "Bear" && tutorialIndexNum === 4) ||  // Bear Tutorial 5 (Challenge) — bear-king capture by dark rhino must end the game on the Dark turn it happens, not wait for the next Light move
                (constellation === "Crane" && tutorialIndexNum === 0) ||
                (constellation === "Tree" && tutorialIndexNum === 0)     // Tree Tutorial 1
                // Add more tutorials here as needed: (constellation === "Snake" && tutorialIndexNum === X) ||
              );
              
              if (needsImmediateCheck) {
                // CRITICAL FIX: Add delay to allow victory detection to complete first
                const timeoutId = setTimeout(() => {
                  // Double-check that tutorial isn't completed after the delay
                  if (tutorialCompleted !== null) return;
                  
                  // SAFETY: Double-check tutorial hasn't changed
                  if (!constellation || !tutorialIndex) return;
                  
                  addToConsole(`🚨 UNIVERSAL IMMEDIATE FAILURE CHECK after Dark turn: ${constellation} Tutorial ${tutorialIndex}`, 'info');
                  
                  // Use the same universal failure detection function
                  // Attach gameState to board for failure logic needing context
                  const boardWithState: any = freshGameState.board;
                  (boardWithState as any).gameState = freshGameState;
                  const hasFailed = checkTutorialFailure(constellation, tutorialIndexNum, freshGameState.turnCount, boardWithState);
                  addToConsole(`🔍 Post-Dark-turn failure check result: ${hasFailed}`, hasFailed ? 'error' : 'info');
                  
                  if (hasFailed) {
                    let failureMessage = `💀 TUTORIAL FAILED!`;
                    let effectPosition: {row: number, col: number} | undefined;
                    
                    if (constellation === "Crane" && tutorialIndexNum === 0) {
                      failureMessage = `💀 TUTORIAL FAILED! Your crane was captured or the dark crane reached row 0!`;
                      // Find dark crane position for race effect - search entire board with detailed logging
                      for (let row = 0; row < freshGameState.board.length; row++) {
                        for (let col = 0; col < freshGameState.board[row].length; col++) {
                          const square = freshGameState.board[row][col];
                          if (square.Pieces.length > 0) {
                          }
                          // Look for dark crane anywhere on the board first
                          if (square.Pieces.some(p => p.type === 'Crane' && !p.isLight)) {
                            effectPosition = { row, col };
                            break;
                          }
                        }
                        if (effectPosition) break;
                      }
                      if (!effectPosition) {
                        effectPosition = { row: 0, col: 0 }; // Fallback to (0,0) which is likely
                      }
                    } else if (constellation === "Bear" && tutorialIndexNum === 2) {
                      failureMessage = `💀 TUTORIAL FAILED! Your bear was captured or the dark goat reached row 0!`;
                      // Predator tutorial: enemy king is dark Goat
                      for (let row = 0; row < freshGameState.board.length; row++) {
                        for (let col = 0; col < freshGameState.board[row].length; col++) {
                          const square = freshGameState.board[row][col];
                          if (square.Pieces.some(p => p.type === 'Goat' && !p.isLight)) {
                            effectPosition = { row, col };
                            break;
                          }
                        }
                        if (effectPosition) break;
                      }
                      if (!effectPosition) {
                        effectPosition = { row: 0, col: 0 };
                      }
                    } else if (constellation === "Tree" && tutorialIndexNum === 0) {
                      failureMessage = `💀 TUTORIAL FAILED! Your tree was captured!`;
                    } else if (constellation === "Bear" && tutorialIndexNum === 4) {
                      // Bear 5 (Challenge): default to the bear-king-capture message;
                      // the dynamic-message logic in setTutorialLossWithDelay (Bear 5
                      // branch) will refine to the rhino-king-race text if that's the
                      // actual cause.
                      failureMessage = `💀 TUTORIAL FAILED! Your bear king was captured!`;
                      // Find the capturing dark rhino's position for the loss-effect
                      // anchor, preferring row 0 (where the king lived).
                      for (let row = 0; row < freshGameState.board.length; row++) {
                        for (let col = 0; col < freshGameState.board[row].length; col++) {
                          const square = freshGameState.board[row][col];
                          if (square.Pieces.some(p => p.type === 'Rhino' && !p.isLight)) {
                            if (row === 0) {
                              effectPosition = { row, col };
                              break;
                            }
                            if (!effectPosition) effectPosition = { row, col };
                          }
                        }
                        if (effectPosition && effectPosition.row === 0) break;
                      }
                    } else {
                      // Check if this is a turn limit failure
                      const tutorialTurnLimit = getTutorialTurnLimit(constellation, tutorialIndexNum);
                      if (tutorialTurnLimit !== 'unlimited' && freshGameState.turnCount >= tutorialTurnLimit) {
                        failureMessage = `💀 TUTORIAL FAILED! Turn limit (${tutorialTurnLimit}) reached without achieving the goal!`;
                      }
                    }
                    
                    setTutorialLossWithDelay(1000, failureMessage, effectPosition);
                  }
                }, 100); // 100ms delay to allow victory detection to complete first
                
                // Track this timeout so it can be cancelled if tutorial changes
                immediateFailureTimeoutsRef.current.push(timeoutId);
              }
            }
            
            // Log popup status after all processing
            addToConsole(`🎯 POPUP STATUS: tutorialCompleted=${tutorialCompleted}, popup should ${tutorialCompleted === 'loss' ? 'show' : 'be hidden'}`, tutorialCompleted === 'loss' ? 'error' : 'info');
          }, 50);
        }
        
        // Check if we've reached the turn limit
        if (turnLimit !== 'unlimited' && gameState.turnCount >= turnLimit) {
          addToConsole(`🚫 Turn limit reached (${gameState.turnCount}/${turnLimit}) - No more moves available!`, 'error');
          
          // DISABLED: Duplicate failure detection - handled by dedicated failure detection
          // if (constellation && tutorialIndex) {
          //   const hasFailed = checkTutorialFailure(...);
          //   if (hasFailed) { setTutorialCompleted('loss'); }
          // }
        } else if (turnLimit !== 'unlimited' && gameState.turnCount >= turnLimit - 1) {
          addToConsole(`Warning: Approaching turn limit (${turnLimit})`, 'warning');
        }
      }
    }
    
    setTurnCount(gameState.turnCount);
  }, [gameState?.turnCount, gameState?.isLightTurn, turnCount, playerMoveCount, turnLimit, isSoloTutorial]);


  useEffect(() => {
    // Apply textures after a short delay to ensure DOM is ready
    const timer = setTimeout(() => {
      applyTerrainToBoard();
    }, 1500); // Increased delay for better reliability
    return () => clearTimeout(timer);
  }, [gameState, constellation, tutorialIndex, applyTerrainToBoard]);

  // Debug popup visibility
  useEffect(() => {
    addToConsole(`🎯 POPUP DEBUG: tutorialCompleted=${tutorialCompleted}`, 'info');
    
    // Special logging for Bear Tutorial 4 (Race defence) immediate failures
    if (constellation === "Bear" && tutorialIndex === "4" && tutorialCompleted === 'loss') {
      addToConsole(`🎯 Bear Tutorial 4: Loss popup should be visible`, 'error');
      
      // Check if the popup component will actually render
      setTimeout(() => {
        const popupElement = document.querySelector('[class*="TutorialLossPopup"]');
        addToConsole(`🎯 UI CHECK: Loss popup in DOM: ${!!popupElement}`, 'error');
      }, 50);
    }

    // Tree Challenge monitoring (can be removed once stable)
    if (constellation === "Tree" && tutorialIndex === "3" && tutorialCompleted === 'loss') {
      addToConsole(`🔍 TREE CHALLENGE: Loss popup shown at turn ${gameState?.turnCount}`, 'info');
    }
  }, [tutorialCompleted, constellation, tutorialIndex]);

  // Precise tutorial failure detection - only check after actual gameplay moves
  useEffect(() => {
    if (!gameState || !constellation || !tutorialIndex || tutorialCompleted !== null || isNavigating) return;
    
    // SAFETY: Only check failures if game has progressed beyond initialization
    if (gameState.turnCount <= 0) return;
    
    // SAFETY: Skip if still loading tutorial (but only block if no gameplay yet)
    if (isLoading && gameState.turnCount <= 0) return;
    
    const tutorialIndexNum = parseInt(tutorialIndex) - 1;
    
    addToConsole(`🔍 UI FAILURE CHECK: ${constellation} tutorial ${tutorialIndexNum + 1}, turn ${gameState.turnCount}`, 'info');
    
    // TEMPORARY: Skip failure check for Bear Tutorial 4 (Race defence) to test victory detection
    if (constellation === "Bear" && tutorialIndexNum === 3) {
      addToConsole(`🔍 SKIPPING UI FAILURE CHECK for Bear Tutorial 4 to test victory detection`, 'info');
      return;
    }

    // Debug: Log the board state for Bear Tutorial 4 (Race defence)
    if (constellation === "Bear" && tutorialIndexNum === 3) {
      const darkCranes = gameState.board.flat()
        .flatMap(square => square.Pieces)
        .filter(piece => piece.type === 'Crane' && !piece.isLight);

      gameState.board.forEach((row, rowIndex) => {
        row.forEach((square, colIndex) => {
          if (square.Pieces.some(p => p.type === 'Crane' && !p.isLight)) {
            addToConsole(`🔍 DARK CRANE found at row ${rowIndex}, col ${colIndex}`, 'info');
          }
        });
      });

      addToConsole(`🔍 Bear Tutorial 4 Debug: Found ${darkCranes.length} dark cranes`, 'info');
    }
    
    // CRITICAL: Check for victory conditions FIRST before checking failures
    // This prevents false failures when player actually won on the turn limit
    const lightPieces = gameState.board.flat()
      .flatMap(square => square.Pieces)
      .filter(piece => piece.isLight);
    
    let hasVictory = false;
    for (const piece of lightPieces) {
      for (let row = 0; row < gameState.board.length; row++) {
        for (let col = 0; col < gameState.board[row].length; col++) {
          const square = gameState.board[row][col];
          if (square.Pieces.includes(piece)) {
            const position = { row, col };
            const winType = checkTutorialWinCondition(
              constellation,
              tutorialIndexNum,
              position,
              piece,
              gameState.turnCount,
              gameState.board
            );
            // CRITICAL FIX: Only treat 'regular' and 'secret' as victories, not 'loss'
            if (winType === 'regular' || winType === 'secret') {
              hasVictory = true;
              addToConsole(`🏆 VICTORY DETECTED: ${winType} - skipping failure check`, 'info');
              break;
            } else if (winType === 'loss') {
              addToConsole(`💀 LOSS DETECTED in win check: ${winType} - continuing to failure check`, 'error');
            }
          }
        }
        if (hasVictory) break;
      }
      if (hasVictory) break;
    }
    
    // Check for failure if no victory was achieved
    if (!hasVictory) {
      const hasFailed = checkTutorialFailure(constellation, tutorialIndexNum, gameState.turnCount, gameState.board);
      
      addToConsole(`🔍 UI FAILURE RESULT: hasFailed=${hasFailed}`, hasFailed ? 'error' : 'info');
      
      if (hasFailed) {
        let failureMessage = "TUTORIAL FAILED!";
        if (constellation === "Bear" && tutorialIndexNum === 1) {
          failureMessage = "TUTORIAL FAILED! Your Bear was captured!";
        } else if (constellation === "Bear" && tutorialIndexNum === 2) {
          failureMessage = "TUTORIAL FAILED! The dark goat king reached row 0!";
        } else if (constellation === "Bear" && tutorialIndexNum === 4) {
          failureMessage = "TUTORIAL FAILED! Both Light Bear kings have been captured!";
        } else if (constellation === "Tree" && tutorialIndexNum === 0) {
          failureMessage = "TUTORIAL FAILED! Your tree was captured!";
        } else {
          // Check if this is a turn limit failure
          const tutorialTurnLimit = getTutorialTurnLimit(constellation, tutorialIndexNum);
          if (tutorialTurnLimit !== 'unlimited' && gameState.turnCount >= tutorialTurnLimit) {
            failureMessage = `TUTORIAL FAILED! Turn limit (${tutorialTurnLimit}) reached without achieving the goal!`;
          }
        }
        
        addToConsole(`💀 ${failureMessage}`, 'error');
        setTutorialLossWithDelay(1000, failureMessage);
        return;
      }
    } else {
      addToConsole(`🔍 UI FAILURE CHECK SKIPPED: Victory detected`, 'info');
    }
    
    // ADDITIONAL CHECK: Enforce absolute turn limit even if no other failures detected
    // This prevents the tutorial from continuing indefinitely
    const tutorialTurnLimit = getTutorialTurnLimit(constellation, tutorialIndexNum);
    if (tutorialTurnLimit !== 'unlimited' && gameState.turnCount >= tutorialTurnLimit && !hasVictory) {
      const turnLimitMessage = `💀 TUTORIAL FAILED! Turn limit (${tutorialTurnLimit}) reached without achieving the goal!`;
      addToConsole(turnLimitMessage, 'error');
      setTutorialLossWithDelay(1000, turnLimitMessage);
      return;
    }
  }, [gameState?.turnCount, gameState?.board, constellation, tutorialIndex, tutorialCompleted]); // Run when turn count OR board changes

  // IMMEDIATE enemy piece failure detection - triggers on any board state change
  useEffect(() => {
    if (!gameState || !constellation || !tutorialIndex || tutorialCompleted !== null || isNavigating) return;
    if (gameState.turnCount <= 0) return;
    
    // SAFETY: Skip if still loading tutorial (but only block if no gameplay yet)
    if (isLoading && gameState.turnCount <= 0) return;
    
    // CRITICAL FIX: Add a delay to allow victory detection to complete first
    const timeoutId = setTimeout(() => {
      // Double-check that tutorial isn't completed after the delay
      if (tutorialCompleted !== null || isNavigating) return;
      
      // SAFETY: Double-check tutorial hasn't changed
      if (!constellation || !tutorialIndex) return;
      
      // SAFETY: Double-check not loading
      if (isLoading) return;
    
      const tutorialIndexNum = parseInt(tutorialIndex) - 1;
      
      // Only run immediate failure detection for tutorials with enemy piece failure conditions
      addToConsole(`🔍 IMMEDIATE CHECK: ${constellation} tutorial ${tutorialIndex}, indexNum=${tutorialIndexNum}`, 'info');
      if ((constellation === "Bear" && tutorialIndexNum === 1) ||  // Bear Tutorial 2 (How to Race)
          (constellation === "Bear" && tutorialIndexNum === 2) ||  // Bear Tutorial 3 (Predator) — dark goat row-0 failure
          (constellation === "Crane" && tutorialIndexNum === 0) ||
          (constellation === "Tree" && tutorialIndexNum === 0)) {   // Tree Tutorial 1
      // CRITICAL FIX: Get fresh board state directly from engine to avoid stale React state
      const freshGameState = ZodiacEngineService.zodiacEngine.getGameState();
      const boardToCheck = freshGameState.board;
      const currentFEN = ZodiacFEN.encode(freshGameState);
      
      addToConsole(`🔄 Using fresh board state from engine for immediate check`, 'info');
      addToConsole(`🎯 IMMEDIATE CHECK FEN: ${currentFEN}`, 'info');
      addToConsole(`🚨 IMMEDIATE FAILURE CHECK: ${constellation} Tutorial ${tutorialIndexNum + 1}, turn ${gameState.turnCount}`, 'info');
      
      if (constellation === "Bear" && tutorialIndexNum === 1) {
        // Bear Tutorial 2 (How to Race): Check for light bear capture
        const lightBears = boardToCheck.flat()
          .flatMap(square => square.Pieces)
          .filter(piece => piece.type === 'Bear' && piece.isLight);
        
        addToConsole(`🔍 Found ${lightBears.length} light bears on board (FRESH STATE)`, 'info');
        
        if (lightBears.length === 0) {
          addToConsole(`🚨 CRITICAL: Light bear captured - triggering immediate failure!`, 'error');
        }
      } else if (constellation === "Crane" && tutorialIndexNum === 0) {
        // Crane Tutorial 1: Check for dark crane at row 0 and light crane capture
        const darkCranes = boardToCheck.flat()
          .flatMap(square => square.Pieces)
          .filter(piece => piece.type === 'Crane' && !piece.isLight);
        
        const lightCranes = boardToCheck.flat()
          .flatMap(square => square.Pieces)
          .filter(piece => piece.type === 'Crane' && piece.isLight);
        
        boardToCheck.forEach((row, rowIndex) => {
          row.forEach((square, colIndex) => {
            if (square.Pieces.some(p => p.type === 'Crane' && !p.isLight)) {
              addToConsole(`🔍 Dark crane at row ${rowIndex}, col ${colIndex} (FRESH STATE)`, 'info');
              
              // CRITICAL UI DEBUG: Log row 0 detection specifically
              if (rowIndex === 0) {
                addToConsole(`🚨 CRITICAL: Dark crane at row 0 - triggering immediate failure!`, 'error');
              }
            }
          });
        });
        
        addToConsole(`🔍 Found ${darkCranes.length} dark cranes and ${lightCranes.length} light cranes on board (FRESH STATE)`, 'info');
        
        if (lightCranes.length === 0) {
          addToConsole(`🚨 CRITICAL: Light crane captured - triggering immediate failure!`, 'error');
        }
      } else if (tutorialIndexNum === 2) {
        // Bear Tutorial 3 (Predator): Check for dark goat at row 0
        const darkGoats = boardToCheck.flat()
          .flatMap(square => square.Pieces)
          .filter(piece => piece.type === 'Goat' && !piece.isLight);
        
        boardToCheck.forEach((row, rowIndex) => {
          row.forEach((square, colIndex) => {
            if (square.Pieces.some(p => p.type === 'Goat' && !p.isLight)) {
              addToConsole(`🔍 Dark goat at row ${rowIndex}, col ${colIndex} (FRESH STATE)`, 'info');
              
              // CRITICAL UI DEBUG: Log row 0 detection specifically
              if (rowIndex === 0) {
                addToConsole(`🚨 CRITICAL: Dark goat king at row 0 - triggering immediate failure!`, 'error');
              }
            }
          });
        });
        
        addToConsole(`🔍 Found ${darkGoats.length} dark goats on board (FRESH STATE)`, 'info');
      } else if (constellation === "Tree" && tutorialIndexNum === 0) {
        // Tree Tutorial 1: Check for light tree capture
        const lightTrees = boardToCheck.flat()
          .flatMap(square => square.Pieces)
          .filter(piece => piece.type === 'Tree' && piece.isLight);
        
        addToConsole(`🔍 Found ${lightTrees.length} light trees on board (FRESH STATE)`, 'info');
        
        if (lightTrees.length === 0) {
          addToConsole(`🚨 CRITICAL: Light tree captured - triggering immediate failure!`, 'error');
        }
      }
      
      // Check for immediate failure using FRESH board state
      // Attach gameState to board for failure logic needing context
      const boardWithState: any = boardToCheck;
      (boardWithState as any).gameState = freshGameState;
      const hasFailed = checkTutorialFailure(constellation, tutorialIndexNum, freshGameState.turnCount, boardWithState);
      
      addToConsole(`🔍 ${constellation} Tutorial ${tutorialIndexNum + 1} immediate failure check result: ${hasFailed}`, hasFailed ? 'error' : 'info');
      
      if (hasFailed) {
        let failureMessage = `💀 TUTORIAL FAILED!`;
        let effectPosition: {row: number, col: number} | undefined;
        
        if (constellation === "Bear" && tutorialIndexNum === 1) {
          failureMessage = `💀 TUTORIAL FAILED! Your bear was captured by the dark bear!`;
          // Find dark bear position for capture effect - search entire board with detailed logging
          for (let row = 0; row < gameState.board.length; row++) {
            for (let col = 0; col < gameState.board[row].length; col++) {
              const square = gameState.board[row][col];
              if (square.Pieces.length > 0) {
              }
              // Look for dark bear anywhere on the board (it's now at the capture position)
              if (square.Pieces.some(p => p.type === 'Bear' && !p.isLight)) {
                effectPosition = { row, col };
                break;
              }
            }
            if (effectPosition) break;
          }
          if (!effectPosition) {
            effectPosition = { row: 2, col: 2 }; // Fallback
          }
        } else if (constellation === "Crane" && tutorialIndexNum === 0) {
          failureMessage = `💀 TUTORIAL FAILED! Your crane was captured or the dark crane reached row 0!`;
          // Find dark crane position for race effect - search entire board with detailed logging
          for (let row = 0; row < gameState.board.length; row++) {
            for (let col = 0; col < gameState.board[row].length; col++) {
              const square = gameState.board[row][col];
              if (square.Pieces.length > 0) {
              }
              // Look for dark crane anywhere on the board first
              if (square.Pieces.some(p => p.type === 'Crane' && !p.isLight)) {
                effectPosition = { row, col };
                break;
              }
            }
            if (effectPosition) break;
          }
          if (!effectPosition) {
            effectPosition = { row: 0, col: 0 }; // Fallback to (0,0) which is likely
          }
        } else if (constellation === "Bear" && tutorialIndexNum === 2) {
          failureMessage = `💀 TUTORIAL FAILED! Your bear was captured or the dark goat reached row 0!`;
          // Predator tutorial: enemy king is dark Goat
          for (let row = 0; row < gameState.board.length; row++) {
            for (let col = 0; col < gameState.board[row].length; col++) {
              const square = gameState.board[row][col];
              if (square.Pieces.some(p => p.type === 'Goat' && !p.isLight)) {
                effectPosition = { row, col };
                break;
              }
            }
            if (effectPosition) break;
          }
          if (!effectPosition) {
            effectPosition = { row: 0, col: 0 };
          }
        } else if (constellation === "Tree" && tutorialIndexNum === 0) {
          failureMessage = `💀 TUTORIAL FAILED! Your tree was captured!`;
        } else {
          // Check if this is a turn limit failure
          const tutorialTurnLimit = getTutorialTurnLimit(constellation, tutorialIndexNum);
          if (tutorialTurnLimit !== 'unlimited' && freshGameState.turnCount >= tutorialTurnLimit) {
            failureMessage = `💀 TUTORIAL FAILED! Turn limit (${tutorialTurnLimit}) reached without achieving the goal!`;
          }
        }
        
        setTutorialLossWithDelay(1000, failureMessage, effectPosition);
        
        return;
      } else {
        addToConsole(`✅ No immediate failure - ${constellation} tutorial continues`, 'info');
      }
      }
    }, 100); // 100ms delay to allow victory detection to complete first
    
    return () => clearTimeout(timeoutId);
  }, [gameState?.board, constellation, tutorialIndex, tutorialCompleted]); // Only trigger on board changes

  // CRITICAL FIX: Monitor engine-set tutorialFailed flag for immediate popup triggering
  useEffect(() => {
    if (!gameState || tutorialCompleted !== null || isNavigating) return;
    
    // Only check for engine failure flag after moves have been made
    // This prevents false positives during tutorial initialization
    if (gameState.turnCount <= 0) return;
    
    // Check if the engine has set the tutorialFailed flag
    const tutorialFailed = (gameState as any).tutorialFailed;
    
    if (tutorialFailed) {
      addToConsole(`🚨 ENGINE FAILURE FLAG DETECTED: Tutorial marked as failed by engine`, 'error');
      addToConsole(`💀 IMMEDIATE POPUP: Setting tutorialCompleted to 'loss' from engine flag`, 'error');
      setTutorialLossWithDelay(1000, 'Tutorial failed due to engine detection');
    }

    // UI fallback: If engine ended game and Dark won (tutorials without configs), trigger loss popup/effect
    if (gameState.state === ("PostGame" as any) && gameState.lightWon === false) {
      addToConsole(`🚨 ENGINE GAME END: Dark victory detected - triggering loss popup/effect`, 'error');
      setTutorialLossWithDelay(1000, 'Tutorial failed! The enemy achieved a win condition.');
    }

    // Handle DRAW state as a tutorial failure (e.g., threefold repetition)
    if (gameState.state === ("Draw" as any)) {
      addToConsole(`🚨 DRAW DETECTED: Threefold repetition - triggering loss popup`, 'error');
      setDynamicLossMessage('You repeated the board position 3 times, leading to a draw. Tutorial failed!');
      setTutorialLossWithDelay(500, 'Draw by threefold repetition');
    }
  }, [gameState, tutorialCompleted, setTutorialCompletedDebug]);


  const handleBackToTutorials = () => {
    const focusConstellation = constellation === 'Fungi' ? 'Mushroom' : constellation;
    navigate('/tutorials', { state: focusConstellation ? { focusConstellation } : undefined });
  };

  const handleVictoryRedo = () => {
    setTutorialCompletedDebug(null);
    restartTutorial();
  };

  const handleVictoryClose = () => {
    
    // CRITICAL: Set navigation flag to block all further processing
    setIsNavigating(true);
    
    // CRITICAL: Clear global loss flag immediately to stop any loops
    (window as any).tutorialLossInProgress = false;
    
    // CRITICAL: Cancel all pending timeouts to prevent stale callbacks
    if (scheduledWinCheckRef.current) {
      clearTimeout(scheduledWinCheckRef.current);
      scheduledWinCheckRef.current = null;
    }
    
    immediateFailureTimeoutsRef.current.forEach(timeoutId => {
      clearTimeout(timeoutId);
    });
    immediateFailureTimeoutsRef.current = [];
    
    // CRITICAL: Reset victory triggered flag to prevent stale state
    victoryTriggeredRef.current = false;
    
    // CRITICAL: Clear any engine tutorial failure flags and FEN history
    const currentGameState = ZodiacEngineService.zodiacEngine.getGameState();
    if (currentGameState) {
      (currentGameState as any).tutorialFailed = false;
      // Clear FEN history to prevent draw data persisting between tutorials
      ZodiacEngineService.zodiacEngine.fenHistoryManager.clearHistory(currentGameState);
    }

    // Comprehensive reset before navigating
    setTutorialCompletedDebug(null);
    setTurnCount(0);
    setPlayerMoveCount(0);
    setIsLoading(true);
    setShowTutorialIntro(false);
    // Clear loss effects
    setActiveLossEffect(null);
    setSuppressTutorialHighlights(false);
    
    
    // Force immediate navigation
    const focusConstellation = constellation === 'Fungi' ? 'Mushroom' : constellation;
    navigate('/tutorials', { state: focusConstellation ? { focusConstellation } : undefined });
  };

  const handleGoHome = () => {
    // Clear FEN history to prevent draw data persisting when leaving tutorials
    const gameState = ZodiacEngineService.zodiacEngine.getGameState();
    if (gameState) {
      ZodiacEngineService.zodiacEngine.fenHistoryManager.clearHistory(gameState);
    }
    navigate('/');
  };

  // Navigate to the next tutorial in the sequence
  const handleNextTutorial = () => {
    // Clear FEN history to prevent draw data persisting between tutorials
    const gameState = ZodiacEngineService.zodiacEngine.getGameState();
    if (gameState) {
      ZodiacEngineService.zodiacEngine.fenHistoryManager.clearHistory(gameState);
    }

    if (!constellation || !tutorialIndex) return;

    const currentIndex = parseInt(tutorialIndex);
    const tutorialsInConstellation = TUTORIAL_TITLES[constellation]?.length || 0;

    // Check if there's a next tutorial in this constellation
    if (currentIndex < tutorialsInConstellation) {
      // Navigate to next tutorial in same constellation
      navigate(`/tutorials/${constellation}/${currentIndex + 1}`);
    } else {
      // No more tutorials in this constellation - go back to star map
      navigate('/tutorials');
    }
  };

  const handleDrop = useCallback((event: React.DragEvent<HTMLDivElement>, position: Position) => {
    // For tutorials, we might want to handle drops differently or disable them
    event.preventDefault();
  }, []);

  const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  }, []);

  const tutorialTitle = constellation && tutorialIndex 
    ? getTutorialTitle(constellation, parseInt(tutorialIndex) - 1)
    : 'Tutorial';

  const tutorialDescription = constellation && tutorialIndex 
    ? getTutorialDescription(constellation, parseInt(tutorialIndex) - 1)
    : 'Learn about this piece in this tutorial.';

  const winDescriptions = constellation && tutorialIndex 
    ? getTutorialWinDescriptions(constellation, parseInt(tutorialIndex) - 1)
    : null;

  if (isLoading) {
    return (
      <div className={`${styles.container} ${darkMode ? styles.dark : ''}`}>
        <div className={styles.loading}>Loading tutorial...</div>
      </div>
    );
  }

  // Mobile layout - simple board-focused view
  if (isMobile) {
    return (
      <div className={`${styles.container} ${darkMode ? styles.dark : ''} ${styles.mobileGamePage}`}>
        <GlobalHeader />

        {/* Back to tutorials button - top left */}
        <button onClick={() => { playUISound(); handleBackToTutorials(); }} className={styles.homeBackButton}>
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
        </button>

        <div className={styles.mobileGameContainer}>
          <div className={styles.boardContainer}>
            <Board
              gameState={gameState}
              uiState={{
                potentialMoves: potentialMoves,
                potentialSideEffects: potentialSideEffects,
              }}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              tutorialInfo={constellation && tutorialIndex ? {
                constellation: constellation,
                tutorialIndex: parseInt(tutorialIndex) - 1,
                hasFirstClickHappened: hasFirstClickHappened,
                activeLossEffect: activeLossEffect,
                suppressTutorialHighlights: suppressTutorialHighlights
              } : undefined}
            />
          </div>

          {/* Tutorial info panel for mobile */}
          <div className={styles.mobileTutorialInfo}>
            <h2 className={styles.mobileTutorialTitle}>{tutorialTitle}</h2>
            {tutorialDescription.includes('|||') ? (
              tutorialDescription.split('|||').map((section, index) => (
                <p key={index} className={styles.mobileTutorialDescription}>{section.trim()}</p>
              ))
            ) : (
              <p className={styles.mobileTutorialDescription}>{tutorialDescription}</p>
            )}
          </div>

          <div className={styles.mobileButtons}>
            <div className={styles.mobileTurnCounter}>
              {turnLimit === 'unlimited' ? (
                <div className={styles.infinityDisplay}>
                  <strong>Moves: </strong>
                  <span className={styles.infinitySymbol}>∞</span>
                </div>
              ) : (
                <>
                  <strong>
                    {isSoloTutorial ? `Moves: ${playerMoveCount}` : `Moves: ${turnCount}`}
                  </strong>
                  <span> / {turnLimit}</span>
                </>
              )}
            </div>
            <button onClick={restartTutorial} className={styles.restartButton}>
              Restart
            </button>
          </div>
        </div>

        {/* AI Thinking Modal */}
        <AIThinking
          isVisible={showAIThinking}
          mode={gameState.mode}
          state={gameState.state}
          centered={true}
          noOffset={true}
        />

        {/* AI Plan Panel — shows the AI's chosen move and its expected
            human response (ply 2 of Sentinel's principal variation). */}
        {showAIPlanPanel && <AIPlanPanel />}

        {/* Victory Popup */}
        {tutorialCompleted && tutorialCompleted !== 'loss' && (
          <div className={`${styles.victoryOverlay} ${
            tutorialCompleted === 'secret' ? styles.secretOverlay : styles.regularOverlay
          }`}>
            <div className={`${styles.victoryPopup} ${
              tutorialCompleted === 'secret' ? styles.secretPopup : styles.regularPopup
            }`}>
              <div className={styles.victoryContent}>
                <div className={styles.victoryIcon}>
                  {tutorialCompleted === 'secret' ? '🌟' : '🎉'}
                </div>
                <h2 className={styles.victoryTitle}>
                  {tutorialCompleted === 'secret' ? 'SECRET VICTORY!' : 'VICTORY!'}
                </h2>
                <p className={styles.victoryMessage}>
                  {tutorialCompleted === 'secret'
                    ? (constellation === 'Bear' && tutorialIndex === '1' ? 'You won by king race!'
                      : constellation === 'Bear' && tutorialIndex === '2' ? 'You won by race victory with moves to spare!'
                      : constellation === 'Bear' && tutorialIndex === '4' ? 'Congratulations! You captured the enemy crane king or raced your bear king to victory!'
                      : getRandomSecretVictoryMessageRandom())
                    : (constellation === 'Bear' && tutorialIndex === '4' ? 'You stopped the enemy dark crane king from racing!'
                      : getRandomRegularVictoryMessage())}
                </p>
                <div className={styles.victoryButtons}>
                  <button
                    onClick={handleVictoryRedo}
                    className={styles.victoryButton}
                  >
                    Try Again
                  </button>
                  <button
                    onClick={handleVictoryClose}
                    className={styles.victoryButton}
                  >
                    Back
                  </button>
                  <button
                    onClick={handleNextTutorial}
                    className={styles.victoryButton}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Loss Popup Component */}
        <TutorialLossPopup
          isVisible={tutorialCompleted === 'loss'}
          tutorialTitle={tutorialTitle}
          lossMessage={dynamicLossMessage || winDescriptions?.loss || "Better luck next time!"}
          onClose={handleVictoryClose}
          onRestart={handleVictoryRedo}
          onNext={handleNextTutorial}
        />

        {/* Tutorial Intro Popup */}
        <TutorialIntro
          isVisible={showTutorialIntro}
          constellation={constellation || ''}
          tutorialIndex={tutorialIndex ? parseInt(tutorialIndex) - 1 : 0}
          tutorialTitle={tutorialTitle}
          pieceType={constellation || ''}
          onClose={() => setShowTutorialIntro(false)}
        />
      </div>
    );
  }

  // Desktop layout
  return (
    <AdSenseLayout>
      <div className={`${styles.container} ${darkMode ? styles.dark : ''}`}>
        <GlobalHeader />

        {/* Back to tutorials button - top left */}
        <button onClick={() => { playUISound(); handleBackToTutorials(); }} className={styles.homeBackButton}>
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
        </button>

        <div className={styles.leftPanel}>
        <h1 className={styles.title}>{tutorialTitle}</h1>
        {tutorialDescription.includes('|||') ? (
          <div className={styles.descriptionSections}>
            {tutorialDescription.split('|||').map((section, index, arr) => (
              <React.Fragment key={index}>
                <p className={styles.description}>{section.trim()}</p>
                {index < arr.length - 1 && <div className={styles.sectionDivider}>✦</div>}
              </React.Fragment>
            ))}
          </div>
        ) : (
          <p className={styles.description}>{tutorialDescription}</p>
        )}
        <div className={styles.turnCounter}>
          {turnLimit === 'unlimited' ? (
            <div className={styles.infinityDisplay}>
              <strong>Moves: </strong>
              <span className={styles.infinitySymbol}>∞</span>
            </div>
          ) : (
            <>
              <strong>
                {isSoloTutorial ? `Moves: ${playerMoveCount}` : `Moves: ${turnCount}`}
              </strong>
              <span> / {turnLimit}</span>
            </>
          )}
        </div>

      </div>

      <div className={styles.gameArea}>
        <div className={styles.gameAreaContent}>
          <div className={styles.boardContainer}>
            <Board
              gameState={gameState}
              uiState={{
                potentialMoves: potentialMoves,
                potentialSideEffects: potentialSideEffects,
              }}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              tutorialInfo={constellation && tutorialIndex ? {
                constellation: constellation,
                tutorialIndex: parseInt(tutorialIndex) - 1,
                hasFirstClickHappened: hasFirstClickHappened,
                activeLossEffect: activeLossEffect,
                suppressTutorialHighlights: suppressTutorialHighlights
              } : undefined}
            />
          </div>

          <div className={styles.boardNavigationButtons}>
            {canGoPrevious() && (
              <button
                onClick={goToPreviousTutorial}
                className={styles.navButton}
                title="Previous Tutorial"
              >
                ← Prev
              </button>
            )}
            <button onClick={restartTutorial} className={styles.restartButton}>
              Restart
            </button>
            {canGoNext() && (
              <button
                onClick={goToNextTutorial}
                className={styles.navButton}
                title="Next Tutorial"
              >
                Next →
              </button>
            )}
          </div>
        </div>
      </div>

      {/* AI Thinking Modal */}
      <AIThinking
        isVisible={showAIThinking}
        mode={gameState.mode}
        state={gameState.state}
        centered={true}
        noOffset={true}
      />

      {/* AI Plan Panel — desktop layout. position:fixed so layout-agnostic. */}
      {showAIPlanPanel && <AIPlanPanel />}

      {/* Victory Popup - Outside all other content */}
      {tutorialCompleted && tutorialCompleted !== 'loss' && (
        <div className={`${styles.victoryOverlay} ${
          tutorialCompleted === 'secret' ? styles.secretOverlay : styles.regularOverlay
        }`}>
          <div className={`${styles.victoryPopup} ${
            tutorialCompleted === 'secret' ? styles.secretPopup : styles.regularPopup
          }`}>
            <div className={styles.victoryContent}>
              <div className={styles.victoryIcon}>
                {tutorialCompleted === 'secret' ? '🌟' : '🎉'}
              </div>
              <h2 className={styles.victoryTitle}>
                {tutorialCompleted === 'secret' ? 'SECRET VICTORY!' : 'VICTORY!'}
              </h2>
              <p className={styles.victoryMessage}>
                {tutorialCompleted === 'secret'
                  ? (constellation === 'Bear' && tutorialIndex === '1' ? 'You won by king race!'
                    : constellation === 'Bear' && tutorialIndex === '2' ? 'You won by race victory with moves to spare!'
                    : constellation === 'Bear' && tutorialIndex === '4' ? 'Congratulations! You captured the enemy crane king or raced your bear king to victory!'
                    : getRandomSecretVictoryMessageRandom())
                  : (constellation === 'Bear' && tutorialIndex === '4' ? 'You stopped the enemy dark crane king from racing!'
                    : getRandomRegularVictoryMessage())}
              </p>
              <div className={styles.victoryButtons}>
                <button
                  onClick={handleVictoryRedo}
                  className={styles.victoryButton}
                >
                  Try Again
                </button>
                <button
                  onClick={handleVictoryClose}
                  className={styles.victoryButton}
                >
                  Back
                </button>
                <button
                  onClick={handleNextTutorial}
                  className={styles.victoryButton}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loss Popup Component */}
      <TutorialLossPopup
        isVisible={tutorialCompleted === 'loss'}
        tutorialTitle={tutorialTitle}
        lossMessage={dynamicLossMessage || winDescriptions?.loss || "Better luck next time!"}
        onClose={handleVictoryClose}
        onRestart={handleVictoryRedo}
        onNext={handleNextTutorial}
      />

      {/* Tutorial Intro Popup */}
      <TutorialIntro
        isVisible={showTutorialIntro}
        constellation={constellation || ''}
        tutorialIndex={tutorialIndex ? parseInt(tutorialIndex) - 1 : 0}
        tutorialTitle={tutorialTitle}
        pieceType={constellation || ''}
        onClose={() => setShowTutorialIntro(false)}
      />
      </div>
    </AdSenseLayout>
  );
}

export default TutorialGame; 
